import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { Volume2, ChevronUp, ChevronDown, Play, Pause, MessageCircle, Eye, EyeOff, Plus, Settings, X, ExternalLink, Link2, Clock, ZoomIn, ZoomOut, RefreshCcw, LocateFixed } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAudio } from '../contexts/AudioContext';
import { useAuth } from '../services/AuthContext';
import { sessionService } from '../services/sessionService';
import { openRouterService } from '../services/openRouterService';
import {
  StructuredClip,
  StructuredEdit,
  StructuredReference,
  StructuredTopic,
  StructuredMutationResponse,
  TtmlMetadataService,
} from '../services/ttmlMetadataService';
import { useTopicVisualization, TopicVisualizationMode, RetentionMode, TimelineOverlayMode } from '../contexts/TopicVisualizationContext';
import * as questionService from '../services/questionService';
import type { SessionQuestion } from '../services/questionService';
import { useAudioUI } from '../contexts/AudioUIContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import { useTimelineViewport } from '../hooks/useTimelineViewport';
import { useDevicePerformance } from '../hooks/useDevicePerformance';
import TimelineComments from './TimelineComments';
import TimelineReferencesWrapper from './TimelineReferencesWrapper';
import AddReferenceModal, { NewReference } from './AddReferenceModal';
import ReferenceEditModal, { ReferenceUpdates } from './ReferenceEditModal';
import ClipEditModal, { ClipEditUpdates } from './ClipEditModal';
import TimestampTooltip from './TimestampTooltip';
import IsolatedCommentsWrapper from './IsolatedCommentsWrapper';
import AnchoredPopover from './AnchoredPopover';
import type { RetentionData } from '../services/youtubeAnalyticsService';
import { getSpeakerColorValue, getSpeakerGradientValue } from '../utils/speakerColors';
import { debugLog } from '../utils/debug';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isDevelopment ? 'http://localhost:7777' : '');
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const SMALL_SPEAKER_GAP_SECONDS = 2;

const toPlainMetadataText = (value?: string | null, preserveLineBreaks = false): string => {
  if (!value) return '';
  if (typeof document === 'undefined') {
    return String(value).trim();
  }

  let decoded = String(value);
  for (let iteration = 0; iteration < 2; iteration += 1) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    const next = textarea.value;
    if (next === decoded) break;
    decoded = next;
  }

  const container = document.createElement('div');
  container.innerHTML = decoded;
  const text = (container.textContent || container.innerText || '').trim();
  if (preserveLineBreaks) {
    return text
      .split('\n')
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n');
  }
  return text.replace(/\s+/g, ' ').trim();
};

// Helper function to format time (handles hours for long content)
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// New TTML revision data returned from the server
interface TtmlRevisionData {
  checksum: string;
  version: number;
  parentChecksum: string;
  rootChecksum: string;
  generatedAt: string;
  model: string;
  revisionNotes: string | null;
  editedBy: string;
  content?: string;
}

interface SearchMatch {
  timestampSeconds: number;
  endTimestampSeconds?: number;
  [key: string]: any;
}

interface CentralAudioPlayerProps {
  sessionId?: string | null;
  speakerSegments?: Map<number, any[]>;
  speakerLabels?: Record<number, string>;
  transcriptionData?: any;
  ttmlFiles?: any[];
  duration?: number;
  selectedTtmlId?: string | null;
  shareToken?: string;
  isPublicView?: boolean;
  retentionData?: RetentionData | null;
  youtubeUrl?: string | null;
  initialComments?: any[];     // Pre-loaded comments from CDN bundle
  fromCdn?: boolean;           // Whether session data came from CDN bundle
  onTtmlRevisionCreated?: (revision: TtmlRevisionData) => void;  // Callback when a new TTML revision is created
  searchMatches?: SearchMatch[];  // Search matches from transcript tab for timeline markers
  gapClusters?: Array<{
    startTime: number;
    endTime: number;
    gapCount: number;
    avgZScore: number;
    severity: string;
    editorialSignal: string;
    gaps?: Array<{
      startTime: number;
      gapDuration: number;
      zScore: number;
    }>;
  }>;
  nonClusteredGaps?: Array<{
    startTime: number;
    gapDuration: number;
    zScore: number;
    speaker: number;
  }>;
}

interface TimelineSegment {
  start: number;
  end: number;
  speaker: number;
}

interface TTMLElement {
  id: string;
  index?: number;
  start: number;
  end: number;
  type: 'clip' | 'edit' | 'topic' | 'reference';
  content: string;
  rank?: number;
  maxRank?: number;
  rankPosition?: number;
  segmentNumber?: number; // Original segment number from TTML
  title?: string;
  // Reference-specific fields (for type === 'reference')
  url?: string;
  linkText?: string;
  category?: string;
  context?: string;
  visualSuggestion?: string;
}

type TimelineLooseElement = Omit<TTMLElement, 'type'> & {
  type: string;
};

interface TimelinePanState {
  pointerId: number;
  lastClientX: number;
  moved: boolean;
}

interface TimelineTouchState {
  mode: 'pan' | 'pinch';
  lastX?: number;
  lastDistance?: number;
  lastCenterX?: number;
  moved: boolean;
}

const normalizeWheelDelta = (delta: number, deltaMode: number, pageSize: number): number => {
  if (!Number.isFinite(delta)) {
    return 0;
  }

  if (deltaMode === 1) {
    return delta * 16;
  }

  if (deltaMode === 2) {
    return delta * pageSize;
  }

  return delta;
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  const editableContainer = target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]');
  return !!editableContainer;
};

const buildTopicContent = (topic: StructuredTopic): string => {
  if (topic.raw) return topic.raw;

  const lines: string[] = [];
  if (topic.segmentNumber != null && topic.title) {
    lines.push(`[${topic.segmentNumber}: ${topic.qualityRank ?? '?'}/${topic.totalTopics ?? '?'}] >> [${toPlainMetadataText(topic.title)}]`);
  } else if (topic.title) {
    lines.push(toPlainMetadataText(topic.title));
  }
  if (topic.score != null) {
    lines.push(`Score: ${topic.score}/10`);
  }
  if (topic.scoreBreakdown) {
    const orderedBreakdownKeys = ['S', 'R', 'C', 'P', 'CR'];
    const breakdownLine = orderedBreakdownKeys
      .map((key) => {
        const entry = topic.scoreBreakdown?.[key];
        return entry ? `${key}: ${entry.score}/${entry.max}` : null;
      })
      .filter(Boolean)
      .join(' | ');
    if (breakdownLine) {
      lines.push(breakdownLine);
    }
  }
  if (topic.justification) {
    lines.push(`Justification: ${toPlainMetadataText(topic.justification, true)}`);
  }
  return lines.join('\n');
};

const buildClipContent = (clip: StructuredClip): string => {
  if (clip.raw) return clip.raw;
  return `[CLIP] ${toPlainMetadataText(clip.title)} | ${toPlainMetadataText(clip.clipType)} | ${toPlainMetadataText(clip.description, true)} | ${toPlainMetadataText(clip.visual, true)}`.trim();
};

const buildEditContent = (edit: StructuredEdit): string => {
  if (edit.raw) return edit.raw;
  return `[EDIT] ${toPlainMetadataText(edit.title)} | ${toPlainMetadataText(edit.suggestion, true)} | ${toPlainMetadataText(edit.improvement, true)} | ${toPlainMetadataText(edit.priority)}`.trim();
};

const buildReferenceContent = (reference: StructuredReference): string => {
  if (reference.raw) return reference.raw;
  const extraLines = [
    toPlainMetadataText(reference.category),
    toPlainMetadataText(reference.context, true),
    toPlainMetadataText(reference.visual, true),
  ].filter(Boolean);
  return [`[REF] ${toPlainMetadataText(reference.name)} | ${toPlainMetadataText(reference.linkText)} | ${reference.url || ''}`.trim(), ...extraLines].join('\n');
};

const mapStructuredTopicToTimelineElement = (topic: StructuredTopic): TTMLElement => ({
  id: `topic_${topic.index}_${topic.startTime}`,
  index: topic.index,
  start: topic.startTime,
  end: topic.endTime,
  type: 'topic',
  content: buildTopicContent(topic),
  title: toPlainMetadataText(topic.title) || undefined,
  rank: topic.score ?? undefined,
  maxRank: 10,
  segmentNumber: topic.segmentNumber ?? undefined,
});

const mapStructuredClipToTimelineElement = (clip: StructuredClip): TTMLElement => ({
  id: `clip_${clip.index}_${clip.startTime}`,
  index: clip.index,
  start: clip.startTime,
  end: clip.endTime,
  type: 'clip',
  content: buildClipContent(clip),
  title: toPlainMetadataText(clip.title) || undefined,
});

const mapStructuredEditToTimelineElement = (edit: StructuredEdit): TTMLElement => ({
  id: `edit_${edit.index}_${edit.startTime}`,
  index: edit.index,
  start: edit.startTime,
  end: edit.endTime,
  type: 'edit',
  content: buildEditContent(edit),
  title: toPlainMetadataText(edit.title) || undefined,
});

const mapStructuredReferenceToTimelineElement = (reference: StructuredReference): TTMLElement => ({
  id: `reference_${reference.index}_${reference.startTime}`,
  index: reference.index,
  start: reference.startTime,
  end: reference.endTime,
  type: 'reference',
  content: buildReferenceContent(reference),
  title: toPlainMetadataText(reference.name) || undefined,
  url: reference.url || undefined,
  linkText: toPlainMetadataText(reference.linkText) || undefined,
  category: toPlainMetadataText(reference.category) || undefined,
  context: toPlainMetadataText(reference.context, true) || undefined,
  visualSuggestion: toPlainMetadataText(reference.visual, true) || undefined,
});

// Separate component for time-sensitive UI updates - memoized to prevent parent re-renders
const AudioProgressDisplay: React.FC<{
  totalDuration: number;
  progressBarRef: React.RefObject<HTMLDivElement>;
  isDarkMode: boolean;
}> = memo(({ totalDuration, progressBarRef, isDarkMode }) => {
  const { currentTime } = useAudio();
  
  return (
    <>
      {/* Progress indicator */}
      <div
        className="absolute top-0 h-full bg-[#FFB52B] transition-all duration-100"
        style={{
          width: `${(currentTime / totalDuration) * 100}%`
        }}
      />
      
      {/* Current time indicator */}
      <div
        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-[#FFB52B] rounded-full shadow-lg"
        style={{
          left: totalDuration > 0 ? `${(currentTime / totalDuration) * 100}%` : '0%',
          marginLeft: '-6px'
        }}
      />
      
      {/* Time display */}
      <div className={`absolute -bottom-6 left-0 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {formatTime(currentTime)} / {formatTime(totalDuration)}
      </div>
    </>
  );
});

// Separate component for the playhead cursor with comment button - memoized
// displayDuration is used for positioning (effectiveDuration in edit mode)
const PlayheadCursor: React.FC<{
  totalDuration: number;
  displayDuration?: number;
}> = memo(({ totalDuration, displayDuration }) => {
  const { currentTime } = useAudio();
  const effectiveDisplayDuration = displayDuration ?? totalDuration;

  return (
    <div
      className="absolute top-0 z-30"
      style={{
        left: effectiveDisplayDuration > 0 ? `${(currentTime / effectiveDisplayDuration) * 100}%` : '0%',
        transform: 'translateX(-50%)'
      }}
    >
      {/* Main cursor line - always visible red */}
      <div className="absolute top-0 w-1 h-full bg-red-500 pointer-events-none" />

      {/* Top arrow - bigger and brighter */}
      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 pointer-events-none">
        <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-red-500" />
      </div>

      {/* Bottom arrow - bigger and brighter */}
      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 pointer-events-none">
        <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-red-500" />
      </div>

    </div>
  );
});

// Wrapper for TimelineComments that gets its own currentTime
const TimelineCommentsWrapper: React.FC<{
  comments: any[];
  duration: number;
  onAddComment: (content: string, timestampSeconds: number) => Promise<void>;
  onSelectComment: (commentId: string) => void;
  selectedCommentId: string | null;
  isDarkMode: boolean;
  width: number;
  height: number;
  viewportStart?: number;
  viewportEnd?: number;
  timeToX?: (time: number) => number;
  onHeightChange?: (height: number) => void;
}> = (props) => {
  const { currentTime } = useAudio();
  return <TimelineComments {...props} currentTime={currentTime} />;
};

// Time display component that updates independently - memoized
// Accepts optional timeMapper function to convert currentTime for display (e.g., for edited timeline)
const TimeDisplay: React.FC<{
  totalDuration: number;
  isDarkMode: boolean;
  displayDuration?: number;
  timeMapper?: (time: number) => number | null;
  showKeyboardHints?: boolean;
}> = memo(({ totalDuration, isDarkMode, displayDuration, timeMapper, showKeyboardHints = true }) => {
  const { currentTime } = useAudio();
  // Use mapped time for display if mapper provided, otherwise use raw currentTime
  const displayTime = timeMapper ? (timeMapper(currentTime) ?? currentTime) : currentTime;
  const effectiveDisplayDuration = displayDuration ?? totalDuration;
  return (
    <div>
      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {formatTime(displayTime)} / {formatTime(effectiveDisplayDuration)}
      </div>
      {showKeyboardHints && (
        <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Space/←/→ for controls
        </div>
      )}
    </div>
  );
});

// Progress bar component - memoized
// displayDuration is used for positioning (effectiveDuration in edit mode)
const ProgressBar: React.FC<{ totalDuration: number; displayDuration?: number }> = memo(({ totalDuration, displayDuration }) => {
  const { currentTime } = useAudio();
  const effectiveDisplayDuration = displayDuration ?? totalDuration;
  if (effectiveDisplayDuration === 0) return null;

  return (
    <div
      className="absolute bottom-0 h-1 bg-orange-500 z-30"
      style={{
        width: `${(currentTime / effectiveDisplayDuration) * 100}%`
      }}
    />
  );
});

const CentralAudioPlayer: React.FC<CentralAudioPlayerProps> = ({
  sessionId,
  speakerSegments,
  speakerLabels,
  transcriptionData,
  ttmlFiles,
  duration,
  selectedTtmlId,
  shareToken,
  isPublicView,
  retentionData,
  youtubeUrl,
  initialComments,
  fromCdn,
  onTtmlRevisionCreated,
  searchMatches = [],
  gapClusters,
  nonClusteredGaps
}) => {
  // Debug: Log when component renders (only when debug enabled)
  debugLog('AUDIO-PLAYER', 'Component rendering with props:', {
    sessionId: sessionId?.substring(0, 8),
    ttmlFilesCount: ttmlFiles?.length || 0,
    selectedTtmlId: selectedTtmlId?.substring(0, 8),
    hasShareToken: !!shareToken,
    isPublicView
  });

  const { isDarkMode } = useDarkMode();
  const { token } = useAuth();
  const { permissions: sharePermissions, shareToken: contextShareToken } = useShareLink();
  const editCuts: any[] = [];
  const hasEditCuts = false;
  const isEditedMode = false;
  const timelineMapper: any = null;

  // Helper to check if an element is fully in a cut region
  const isElementInCutRegion = (start: number, end: number): boolean => {
    if (!timelineMapper || !hasEditCuts) return false;
    return timelineMapper.isFullyInCutRegion(start, end);
  };

  // Use props first, then context as fallback
  const effectiveShareToken = shareToken || contextShareToken;
  const effectiveIsPublicView = isPublicView !== undefined ? isPublicView : !!sharePermissions;
  const fetchTtmlContent = useCallback(async (checksum: string): Promise<string | null> => {
    if (effectiveIsPublicView) {
      if (!effectiveShareToken) {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/public/share/${effectiveShareToken}/ttml-content/${checksum}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to fetch TTML: ${response.status}`);
      }

      return response.text();
    }

    if (!token) {
      return null;
    }

    const blob = await openRouterService.downloadCachedTTML(checksum, token);
    return blob.text();
  }, [effectiveIsPublicView, effectiveShareToken, token]);
  
  const {
    loadAudio,
    play,
    pause,
    seekTo,
    duration: audioDuration,
    isPlaying,
    volume,
    setVolume,
    currentTime,
    playbackRate,
    setPlaybackRate,
    playerMode
  } = useAudio();
  const { isCompactTouchLayout } = useDevicePerformance();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [hoveredTime, setHoveredTime] = useState<number | null>(null);
  const [hoveredTopic, setHoveredTopic] = useState<TTMLElement | null>(null);
  const [hoveredClip, setHoveredClip] = useState<TTMLElement | null>(null);
  const [hoveredEdit, setHoveredEdit] = useState<TTMLElement | null>(null);
  const [hoveredReference, setHoveredReference] = useState<TTMLElement | null>(null);
  const [hoveredQuestionSpan, setHoveredQuestionSpan] = useState<{ questionNumber: number; question: SessionQuestion } | null>(null);
  const [selectedReference, setSelectedReference] = useState<TTMLElement | null>(null);
  const [showReferencesModal, setShowReferencesModal] = useState(false);
  const [showAddReferenceModal, setShowAddReferenceModal] = useState(false);
  // Clip/Edit modal state
  const [selectedClipEdit, setSelectedClipEdit] = useState<{ element: TTMLElement; type: 'clip' | 'edit' } | null>(null);
  const [showClipEditModal, setShowClipEditModal] = useState(false);
  // Clip/Edit drag state
  const [elementDragState, setElementDragState] = useState<{
    element: TTMLElement;
    type: 'clip' | 'edit';
    handle: 'left' | 'right' | 'move';
    initialStart: number;
    initialEnd: number;
    initialMouseX: number;
  } | null>(null);
  const [elementDragPreview, setElementDragPreview] = useState<{ start: number; end: number } | null>(null);
  const [elementDragTooltipPosition, setElementDragTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  // Floating action popup for clips/edits (appears after hover delay)
  const [floatingActionElement, setFloatingActionElement] = useState<{ element: TTMLElement; type: 'clip' | 'edit'; x: number; y: number } | null>(null);
  const floatingActionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [addReferenceTimestamp, setAddReferenceTimestamp] = useState<number>(0);
  const [ttmlElements, setTtmlElements] = useState<{ clips: TTMLElement[], topics: TTMLElement[], edits: TTMLElement[], references: TTMLElement[] }>({ clips: [], topics: [], edits: [], references: [] });
  const [questionData, setQuestionData] = useState<SessionQuestion[]>([]);
  const [selectedTtmlFile, setSelectedTtmlFile] = useState<string | null>(null);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showTimelineMenu, setShowTimelineMenu] = useState(false);
  const speedMenuRef = useRef<HTMLDivElement>(null);
  const timelineMenuRef = useRef<HTMLButtonElement>(null);
  const speedOptions = [1, 1.5, 2, 2.5];
  
  // Debug: Log when CentralAudioPlayer re-renders - commented out for performance
  // useEffect(() => {
  //   console.log('[CentralAudioPlayer] Component rendered at', new Date().toISOString());
  //   console.log('[CentralAudioPlayer] Props:', { shareToken, isPublicView });
  //   console.log('[CentralAudioPlayer] Context:', { contextShareToken, sharePermissions: !!sharePermissions });
  //   console.log('[CentralAudioPlayer] Effective:', { effectiveShareToken, effectiveIsPublicView });
  // });
  const [currentTtmlContent, setCurrentTtmlContent] = useState<string | null>(null);
  const [lastLoadedChecksum, setLastLoadedChecksum] = useState<string | null>(null);
  // Check if comments are allowed for public shares
  const commentsAllowed = !effectiveIsPublicView || (sharePermissions?.commentsRead ?? true);
  
  const [showTimelineComments, setShowTimelineComments] = useState(() => {
    if (typeof window === 'undefined') {
      return commentsAllowed;
    }
    return commentsAllowed && !isCompactTouchLayout;
  });
  const [comments, setComments] = useState<any[]>([]);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const [pendingCommentTime, setPendingCommentTime] = useState<number | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [timelineHeight, setTimelineHeight] = useState(60);
  const {
    mode: topicVisualizationMode,
    showGradient,
    setMode: setTopicVisualizationMode,
    setShowGradient,
    retentionMode,
    setRetentionMode,
    setRetentionData: setContextRetentionData,
    overlayMode,
    setOverlayMode
  } = useTopicVisualization();

  // Sync retentionData prop to context so ReadableTranscriptView can use it
  useEffect(() => {
    setContextRetentionData(retentionData || null);
  }, [retentionData, setContextRetentionData]);

  // Fetch question data for the session (always fetch so toggle shows count)
  useEffect(() => {
    if (!sessionId) {
      setQuestionData([]);
      return;
    }

    let cancelled = false;
    const fetchPromise = effectiveIsPublicView && effectiveShareToken
      ? questionService.listPublicShareQuestions(effectiveShareToken)
      : questionService.listQuestions(sessionId, token || undefined);

    fetchPromise
      .then((response) => {
        if (!cancelled) {
          const timed = response.questions
            .filter((q) => q.timestampSeconds !== null && Number.isFinite(q.timestampSeconds))
            .sort((a, b) => (a.timestampSeconds as number) - (b.timestampSeconds as number));
          setQuestionData(timed);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[CentralAudioPlayer] Failed to load questions:', err.message);
          setQuestionData([]);
        }
      });

    return () => { cancelled = true; };
  }, [sessionId, token, effectiveIsPublicView, effectiveShareToken]);

  useEffect(() => {
    if (overlayMode === 'questions' && questionData.length === 0) {
      setOverlayMode('topics');
    }
  }, [overlayMode, questionData.length, setOverlayMode]);

  // Cycle through retention modes (stored in context for sync with ReadableTranscriptView)
  const cycleRetentionMode = () => {
    const modes: RetentionMode[] = ['off', 'relative', 'absolute', 'adjusted', 'drops'];
    const currentIndex = modes.indexOf(retentionMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setRetentionMode(modes[nextIndex]);
  };

  // Get retention mode display info
  const getRetentionModeInfo = () => {
    switch (retentionMode) {
      case 'relative': return { label: 'Rel', color: 'bg-[#314159]', title: 'Relative retention (vs similar videos)' };
      case 'absolute': return { label: 'Abs', color: 'bg-[#232E3F]', title: 'Absolute retention (% still watching)' };
      case 'adjusted': return { label: 'Adj', color: 'bg-[#8a6115]', title: 'Adjusted (relative × √absolute)' };
      case 'drops': return { label: 'Drops', color: 'bg-[#7b3f32]', title: 'Viewer drops (started/stopped watching)' };
      case 'off':
      default: return { label: 'Off', color: 'bg-gray-600', title: 'Retention hidden' };
    }
  };
  const hasTimelineGraphOverlay = Boolean(
    retentionData?.retentionData?.length
    && retentionMode !== 'off'
  );
  const isCompactTimeline = isCompactTouchLayout && !hasTimelineGraphOverlay;
  const timelineViewportHeightClass = isCompactTimeline ? 'h-12' : 'h-24';
  const timelineLayout = useMemo(() => {
    if (isCompactTimeline) {
      return {
        topicTop: '0px',
        topicHeight: '10px',
        speakerTop: '10px',
        speakerHeight: '10px',
        editTop: '22px',
        editHeight: '5px',
        clipTop: '29px',
        clipHeight: '5px',
        gapBarBottom: '8px',
        gapBarHeight: '3px',
        gapTickBottom: '11px',
        nonClusteredGapBottom: '8px',
      };
    }

    return {
      topicTop: '0%',
      topicHeight: '33.333333%',
      speakerTop: '0%',
      speakerHeight: '100%',
      editTop: '25%',
      editHeight: '25%',
      clipTop: '25%',
      clipHeight: '25%',
      gapBarBottom: '10px',
      gapBarHeight: '6px',
      gapTickBottom: '16px',
      nonClusteredGapBottom: '10px',
    };
  }, [isCompactTimeline]);
  const {
    hoveredTimeRange,
    setDragHighlightTime,
    cursorSyncEnabled,
    setCursorSyncEnabled,
    syncRequestId,
    requestCursorSync,
  } = useAudioUI();

  const progressBarRef = useRef<HTMLDivElement>(null);
  const timelineSecondaryViewportRef = useRef<HTMLDivElement>(null);
  const [progressBarWidth, setProgressBarWidth] = useState(0);
  const [structuredLoadFailed, setStructuredLoadFailed] = useState(false);
  const timelinePanStateRef = useRef<TimelinePanState | null>(null);
  const timelineTouchStateRef = useRef<TimelineTouchState | null>(null);
  const suppressTimelineClickRef = useRef(false);
  const lastHandledSyncRequestIdRef = useRef(0);
  
  // Track progressBar width
  useEffect(() => {
    const updateWidth = () => {
      if (progressBarRef.current) {
        const newWidth = progressBarRef.current.clientWidth;
        setProgressBarWidth(newWidth);
      }
    };
    
    // Check immediately and after a short delay
    updateWidth();
    const timer = setTimeout(updateWidth, 100);
    
    // Also update on window resize
    window.addEventListener('resize', updateWidth);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(() => {
    if (!commentsAllowed) {
      setShowTimelineComments(false);
      return;
    }

    if (isCompactTouchLayout) {
      setShowTimelineComments(false);
    }
  }, [commentsAllowed, isCompactTouchLayout]);
  
  // Get current time only when needed for keyboard shortcuts
  const audioContext = useAudio();
  const getCurrentTime = useCallback(() => audioContext.currentTime, [audioContext]);

  // Generate timeline segments data
  const generateTimelineData = useCallback(() => {
    if (!speakerSegments || (speakerSegments instanceof Map ? speakerSegments.size === 0 : Object.keys(speakerSegments).length === 0)) {
      return { segments: [], totalDuration: duration || 0 };
    }

    const allSegments: TimelineSegment[] = [];

    // Handle both Map and plain object (share bundles may pass plain objects)
    const entries = speakerSegments instanceof Map
      ? Array.from(speakerSegments.entries())
      : Object.entries(speakerSegments);

    for (const [speakerId, segments] of entries) {
      const segArray = Array.isArray(segments) ? segments : (segments ? [segments] : []);
      for (const segment of segArray) {
        if (segment && typeof segment.start === 'number' && typeof segment.end === 'number') {
          allSegments.push({
            start: segment.start,
            end: segment.end,
            speaker: typeof speakerId === 'number' ? speakerId : parseInt(String(speakerId), 10) || 0
          });
        }
      }
    }
    
    allSegments.sort((a, b) => a.start - b.start);
    
    const totalDuration = duration || audioDuration || (allSegments.length > 0 
      ? Math.max(...allSegments.map(s => s.end))
      : 0);
    
    return { segments: allSegments, totalDuration };
  }, [speakerSegments, duration, audioDuration]);

  const { segments: timelineSegments, totalDuration } = generateTimelineData();

  // Compute effective duration for display (edited timeline when in edit mode)
  const effectiveDuration = useMemo(() => {
    if (isEditedMode && hasEditCuts && timelineMapper) {
      return timelineMapper.getStats().editedDuration;
    }
    return totalDuration;
  }, [isEditedMode, hasEditCuts, timelineMapper, totalDuration]);
  const timelineDisplayDuration = isEditedMode ? effectiveDuration : totalDuration;

  const {
    viewportStart,
    viewportEnd,
    viewportDuration,
    contentWidth: timelineContentWidth,
    contentOffsetX: timelineContentOffsetX,
    isZoomed: isTimelineZoomed,
    timeToX,
    xToTime,
    resetZoom,
    panByPixels,
    zoomAroundTime,
    centerOnTime,
    markUserInteracted,
  } = useTimelineViewport({
    totalDuration: timelineDisplayDuration,
    visibleWidth: progressBarWidth,
    currentTime,
    isMobile: isCompactTouchLayout,
    autoFollowEnabled: cursorSyncEnabled,
  });
  const canEditTimelineMetadata = !!(token && !effectiveIsPublicView && lastLoadedChecksum);

  const clearHoveredTimelineState = useCallback(() => {
    setHoveredTime(null);
    setHoveredTopic(null);
    setHoveredClip(null);
    setHoveredEdit(null);
    setHoveredReference(null);
    setHoveredQuestionSpan(null);
  }, []);

  const updateHoveredTimelineState = useCallback((originalTime: number | null) => {
    if (originalTime === null) {
      clearHoveredTimelineState();
      return;
    }

    setHoveredTime(originalTime);
    setHoveredTopic(ttmlElements.topics.find((topic) => originalTime >= topic.start && originalTime <= topic.end) || null);
    setHoveredClip(ttmlElements.clips.find((clip) => originalTime >= clip.start && originalTime <= clip.end) || null);
    setHoveredEdit(ttmlElements.edits.find((edit) => originalTime >= edit.start && originalTime <= edit.end) || null);
    setHoveredReference(ttmlElements.references.find((reference) => originalTime >= reference.start && originalTime <= reference.end) || null);

    // Find hovered question span from raw question data
    if (overlayMode === 'questions' && questionData.length > 0) {
      let matchedIdx = -1;
      for (let i = 0; i < questionData.length; i++) {
        const ts = questionData[i].timestampSeconds as number;
        const nextTs = i < questionData.length - 1 ? (questionData[i + 1].timestampSeconds as number) : Infinity;
        if (originalTime >= ts && originalTime < nextTs) {
          matchedIdx = i;
          break;
        }
      }
      setHoveredQuestionSpan(matchedIdx >= 0
        ? { questionNumber: matchedIdx + 1, question: questionData[matchedIdx] }
        : null
      );
    } else {
      setHoveredQuestionSpan(null);
    }
  }, [clearHoveredTimelineState, ttmlElements, overlayMode, questionData]);

  const getTimelineClientPosition = useCallback((clientX: number) => {
    if (!progressBarRef.current) {
      return null;
    }

    const rect = progressBarRef.current.getBoundingClientRect();
    return {
      rect,
      localX: clamp(clientX - rect.left, 0, rect.width),
    };
  }, []);

  const visiblePlayheadX = useMemo(() => {
    return clamp(timeToX(currentTime), 0, progressBarWidth);
  }, [currentTime, progressBarWidth, timeToX]);

  const hoveredDisplayTime = useMemo(() => {
    if (hoveredTime === null) {
      return null;
    }

    if (isEditedMode && timelineMapper) {
      return timelineMapper.originalToEdited(hoveredTime) ?? hoveredTime;
    }

    return hoveredTime;
  }, [hoveredTime, isEditedMode, timelineMapper]);

  const hoveredDisplayX = useMemo(() => {
    if (hoveredDisplayTime === null) {
      return null;
    }

    return clamp(timeToX(hoveredDisplayTime), 0, progressBarWidth);
  }, [hoveredDisplayTime, progressBarWidth, timeToX]);
  const hoveredSpeaker = useMemo<{ id: number; label: string; color: string } | null>(() => {
    if (hoveredTime === null) {
      return null;
    }

    const activeSegment = timelineSegments.find((segment) => (
      hoveredTime >= segment.start && hoveredTime < segment.end
    ));
    let resolvedSegment = activeSegment || null;

    if (!resolvedSegment) {
      let previousSegment: typeof timelineSegments[number] | null = null;
      let nextSegment: typeof timelineSegments[number] | null = null;

      for (const segment of timelineSegments) {
        if (segment.end <= hoveredTime) {
          previousSegment = segment;
          continue;
        }

        if (segment.start > hoveredTime) {
          nextSegment = segment;
          break;
        }
      }

      if (
        previousSegment
        && nextSegment
        && hoveredTime >= previousSegment.end
        && hoveredTime < nextSegment.start
        && (nextSegment.start - previousSegment.end) <= SMALL_SPEAKER_GAP_SECONDS
      ) {
        resolvedSegment = previousSegment;
      }
    }

    if (!resolvedSegment) {
      return null;
    }

    const speakerId = Number(resolvedSegment.speaker);
    return {
      id: speakerId,
      label: speakerLabels?.[speakerId] || `Speaker ${speakerId}`,
      color: getSpeakerColorValue(speakerId, isDarkMode),
    };
  }, [hoveredTime, isDarkMode, speakerLabels, timelineSegments]);

  // Helper to remap time to edited timeline
  const remapToEditedTime = useCallback((originalTime: number): number | null => {
    if (!isEditedMode || !timelineMapper || !hasEditCuts) {
      return originalTime;
    }
    return timelineMapper.originalToEdited(originalTime);
  }, [isEditedMode, timelineMapper, hasEditCuts]);

  // Edit-aware topics with proper sorting
  const editAwareTopics = useMemo(() => {
    if (!isEditedMode || !timelineMapper || !hasEditCuts) {
      return ttmlElements.topics;
    }

    return ttmlElements.topics
      .filter(topic => !timelineMapper.isFullyInCutRegion(topic.start, topic.end))
      .map(topic => {
        let editedStart = timelineMapper.originalToEdited(topic.start);
        const editedEnd = timelineMapper.originalToEdited(topic.end);

        // If start is in a cut region but end is not, use the cut's end time
        if (editedStart === null && editedEnd !== null) {
          const cut = timelineMapper.getCutAt(topic.start);
          if (cut) {
            editedStart = timelineMapper.originalToEdited(cut.endTime);
          }
        }

        const effectiveStart = editedStart ?? topic.start;
        const effectiveEnd = editedEnd ?? topic.end;

        return {
          ...topic,
          _originalStart: topic.start,
          _originalEnd: topic.end,
          start: effectiveStart,
          end: effectiveEnd,
          _editedStart: effectiveStart
        };
      })
      // Sort by edited start time to maintain chronological order
      .sort((a, b) => (a as any)._editedStart - (b as any)._editedStart);
  }, [ttmlElements.topics, isEditedMode, timelineMapper, hasEditCuts]);

  // Question spans: contiguous spans from one question timestamp to the next
  const questionSpans = useMemo(() => {
    if (questionData.length === 0 || totalDuration === 0) return [];

    const spans: Array<{
      id: string;
      start: number;
      end: number;
      questionNumber: number;
      question: SessionQuestion;
    }> = [];

    for (let i = 0; i < questionData.length; i++) {
      const q = questionData[i];
      const start = q.timestampSeconds as number;
      const end = i < questionData.length - 1
        ? (questionData[i + 1].timestampSeconds as number)
        : totalDuration;
      spans.push({
        id: `qspan_${i}`,
        start,
        end: Math.max(end, start + 1), // Ensure minimum width
        questionNumber: i + 1,
        question: q,
      });
    }

    return spans;
  }, [questionData, totalDuration]);

  // Edit-aware question spans with proper time remapping
  const editAwareQuestionSpans = useMemo(() => {
    if (!isEditedMode || !timelineMapper || !hasEditCuts) {
      return questionSpans;
    }

    return questionSpans
      .filter(span => !timelineMapper.isFullyInCutRegion(span.start, span.end))
      .map(span => {
        let editedStart = timelineMapper.originalToEdited(span.start);
        const editedEnd = timelineMapper.originalToEdited(span.end);

        if (editedStart === null && editedEnd !== null) {
          const cut = timelineMapper.getCutAt(span.start);
          if (cut) {
            editedStart = timelineMapper.originalToEdited(cut.endTime);
          }
        }

        const effectiveStart = editedStart ?? span.start;
        const effectiveEnd = editedEnd ?? span.end;

        return { ...span, start: effectiveStart, end: effectiveEnd };
      })
      .sort((a, b) => a.start - b.start);
  }, [questionSpans, isEditedMode, timelineMapper, hasEditCuts]);

  // Edit-aware speaker segments with proper time remapping
  const editAwareSpeakerSegments = useMemo(() => {
    if (!isEditedMode || !timelineMapper || !hasEditCuts) {
      return timelineSegments;
    }

    return timelineSegments
      .filter(segment => !timelineMapper.isFullyInCutRegion(segment.start, segment.end))
      .map(segment => {
        let editedStart = timelineMapper.originalToEdited(segment.start);
        const editedEnd = timelineMapper.originalToEdited(segment.end);

        // If start is in a cut region but end is not, use the cut's end time
        if (editedStart === null && editedEnd !== null) {
          const cut = timelineMapper.getCutAt(segment.start);
          if (cut) {
            editedStart = timelineMapper.originalToEdited(cut.endTime);
          }
        }

        const effectiveStart = editedStart ?? segment.start;
        const effectiveEnd = editedEnd ?? segment.end;

        return {
          ...segment,
          _originalStart: segment.start,
          _originalEnd: segment.end,
          start: effectiveStart,
          end: effectiveEnd
        };
      })
      // Sort by edited start time
      .sort((a, b) => a.start - b.start);
  }, [timelineSegments, isEditedMode, timelineMapper, hasEditCuts]);

  // Calculate speaker contributions
  const speakerContributions = useCallback(() => {
    if (!timelineSegments.length || !totalDuration) return {};
    
    const contributions: Record<number, number> = {};
    let totalSpeakingTime = 0;
    
    timelineSegments.forEach(segment => {
      const duration = segment.end - segment.start;
      contributions[segment.speaker] = (contributions[segment.speaker] || 0) + duration;
      totalSpeakingTime += duration;
    });
    
    // Convert to percentages
    const percentages: Record<number, number> = {};
    Object.entries(contributions).forEach(([speaker, time]) => {
      percentages[parseInt(speaker)] = Math.round((time / totalSpeakingTime) * 100);
    });
    
    return percentages;
  }, [timelineSegments, totalDuration]);

  const speakerPercentages = speakerContributions();

  // Load session audio when sessionId changes
  // For public shares, audio is loaded by App.tsx with the correct relative URL
  // We only need to load audio here for authenticated (non-share) views
  useEffect(() => {
    // Skip if public share view - App.tsx handles audio loading for shares
    if (effectiveShareToken) return;
    if (!sessionId || !token) return;

    const loadSessionAudio = async () => {
      try {
        const sessionData = await sessionService.getSession(sessionId, token);
        if (sessionData.audioUrl) {
          loadAudio(sessionData.audioUrl);
        }
      } catch (error) {
        console.error('Failed to load session audio:', error);
      }
    };

    loadSessionAudio();
  }, [sessionId, token, loadAudio, effectiveShareToken]);

  // Keyboard shortcuts
  useEffect(() => {
    const isShortcutBlocked = (event: KeyboardEvent) => (
      event.defaultPrevented
      || isEditableTarget(event.target)
      || isEditableTarget(document.activeElement)
    );

    const handleKeyPress = (e: KeyboardEvent) => {
      if (isShortcutBlocked(e)) {
        return;
      }

      switch (e.code) {
        case 'Space':
          if (e.repeat) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          if (isPlaying) {
            pause();
          } else {
            play();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          e.stopPropagation();
          seekTo(Math.max(0, getCurrentTime() - 15));
          break;
        case 'ArrowRight':
          e.preventDefault();
          e.stopPropagation();
          seekTo(Math.min(totalDuration, getCurrentTime() + 15));
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isShortcutBlocked(e)) {
        return;
      }

      if (e.code === 'Space' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const listenerOptions = { capture: true } as const;
    window.addEventListener('keydown', handleKeyPress, listenerOptions);
    window.addEventListener('keyup', handleKeyUp, listenerOptions);
    return () => {
      window.removeEventListener('keydown', handleKeyPress, listenerOptions);
      window.removeEventListener('keyup', handleKeyUp, listenerOptions);
    };
  }, [isPlaying, play, pause, seekTo, getCurrentTime, totalDuration]);

  // Handle floating menus click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(event.target as Node)) {
        setShowSpeedMenu(false);
      }
    };

    if (showSpeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSpeedMenu]);

  const handleTimelineWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const position = getTimelineClientPosition(event.clientX);
    if (!position || timelineDisplayDuration === 0) {
      return;
    }

    event.preventDefault();

    const normalizedDeltaX = normalizeWheelDelta(event.deltaX, event.deltaMode, position.rect.width);
    const normalizedDeltaY = normalizeWheelDelta(event.deltaY, event.deltaMode, position.rect.height);
    const isPinchZoomGesture = event.ctrlKey;
    const isPanGesture = !isPinchZoomGesture && (event.metaKey || event.shiftKey || Math.abs(normalizedDeltaX) > Math.abs(normalizedDeltaY));

    setCursorSyncEnabled(false);
    markUserInteracted();

    if (isPanGesture) {
      const panDelta = Math.abs(normalizedDeltaX) > Math.abs(normalizedDeltaY)
        ? normalizedDeltaX
        : normalizedDeltaY;
      panByPixels(panDelta);
      return;
    }

    const focusTime = xToTime(position.localX);
    const clampedZoomDelta = clamp(normalizedDeltaY, -200, 200);
    const scale = Math.exp(clampedZoomDelta * 0.003);
    zoomAroundTime(focusTime, scale);
  }, [
    getTimelineClientPosition,
    markUserInteracted,
    panByPixels,
    setCursorSyncEnabled,
    timelineDisplayDuration,
    xToTime,
    zoomAroundTime,
  ]);

  useEffect(() => {
    if (!syncRequestId || lastHandledSyncRequestIdRef.current === syncRequestId) {
      return;
    }

    lastHandledSyncRequestIdRef.current = syncRequestId;
    if (isTimelineZoomed) {
      centerOnTime(currentTime);
    }
  }, [centerOnTime, currentTime, isTimelineZoomed, syncRequestId]);

  // Removed mock waveform generation

  // Handle timeline click - new behavior
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (suppressTimelineClickRef.current) {
      suppressTimelineClickRef.current = false;
      return;
    }

    const position = getTimelineClientPosition(e.clientX);
    if (!position || timelineDisplayDuration === 0) return;

    const clickedTime = xToTime(position.localX);
    let originalTime = clickedTime;
    if (isEditedMode && timelineMapper) {
      const converted = timelineMapper.editedToOriginal(clickedTime);
      if (converted !== null) {
        originalTime = converted;
      }
    }
    const isNearPlayhead = Math.abs(position.localX - visiblePlayheadX) < 18;

    if (isPlaying && isNearPlayhead) {
      // If playing and clicked near playhead, stop
      pause();
    } else {
      // Otherwise, seek to clicked position and play
      // In edit mode, clickedTime is already in edited timeline (what YouTube player expects)
      seekTo(clickedTime);
      requestCursorSync(clickedTime);
      if (!isPlaying) {
        play();
      }
    }

    if (isCompactTouchLayout) {
      updateHoveredTimelineState(originalTime);
    }
  };

  // Handle timeline hover
  const handleTimelineHover = (e: React.MouseEvent<HTMLDivElement>) => {
    const position = getTimelineClientPosition(e.clientX);
    if (!position || timelineDisplayDuration === 0) return;

    const editedTime = xToTime(position.localX);

    // For element lookup and seeking, we need original time
    // In edit mode, convert edited time back to original time
    let originalTime = editedTime;
    if (isEditedMode && timelineMapper) {
      const converted = timelineMapper.editedToOriginal(editedTime);
      if (converted !== null) {
        originalTime = converted;
      }
    }

    updateHoveredTimelineState(originalTime);
  };

  const shouldIgnoreTimelinePanTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return !!target.closest('button, a, input, textarea, select, [role="button"], [data-timeline-pan-ignore="true"]');
  }, []);

  const handleTimelinePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isTimelineZoomed || e.button !== 0 || shouldIgnoreTimelinePanTarget(e.target)) {
      return;
    }

    timelinePanStateRef.current = {
      pointerId: e.pointerId,
      lastClientX: e.clientX,
      moved: false,
    };

    e.currentTarget.setPointerCapture(e.pointerId);
  }, [isTimelineZoomed, shouldIgnoreTimelinePanTarget]);

  const handleTimelinePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const panState = timelinePanStateRef.current;
    if (!panState || panState.pointerId !== e.pointerId) {
      return;
    }

    const deltaX = e.clientX - panState.lastClientX;
    if (Math.abs(deltaX) < 0.5) {
      return;
    }

    panState.lastClientX = e.clientX;
    panState.moved = panState.moved || Math.abs(deltaX) > 3;
    markUserInteracted();
    setCursorSyncEnabled(false);
    panByPixels(-deltaX);
  }, [markUserInteracted, panByPixels, setCursorSyncEnabled]);

  const finishTimelinePointerPan = useCallback((pointerId: number, currentTarget: HTMLDivElement) => {
    const panState = timelinePanStateRef.current;
    if (!panState || panState.pointerId !== pointerId) {
      return;
    }

    suppressTimelineClickRef.current = panState.moved;
    timelinePanStateRef.current = null;

    if (currentTarget.hasPointerCapture(pointerId)) {
      currentTarget.releasePointerCapture(pointerId);
    }
  }, []);

  const getTouchMetrics = useCallback((touches: React.TouchList) => {
    const first = touches[0];
    if (!first) {
      return null;
    }

    const position = getTimelineClientPosition(first.clientX);
    if (!position) {
      return null;
    }

    const second = touches[1];
    if (!second) {
      return {
        localX: position.localX,
      };
    }

    const secondLocalX = clamp(second.clientX - position.rect.left, 0, position.rect.width);
    return {
      localX: position.localX,
      centerX: clamp((position.localX + secondLocalX) / 2, 0, position.rect.width),
      distance: Math.abs(secondLocalX - position.localX),
    };
  }, [getTimelineClientPosition]);

  const handleTimelineTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (shouldIgnoreTimelinePanTarget(e.target)) {
      return;
    }

    if (e.touches.length === 2) {
      const metrics = getTouchMetrics(e.touches);
      if (!metrics || metrics.centerX === undefined || metrics.distance === undefined) {
        return;
      }

      timelineTouchStateRef.current = {
        mode: 'pinch',
        lastCenterX: metrics.centerX,
        lastDistance: Math.max(metrics.distance, 1),
        moved: false,
      };
      return;
    }

    if (e.touches.length === 1 && isTimelineZoomed) {
      const metrics = getTouchMetrics(e.touches);
      if (!metrics) {
        return;
      }

      timelineTouchStateRef.current = {
        mode: 'pan',
        lastX: metrics.localX,
        moved: false,
      };
    }
  }, [getTouchMetrics, isTimelineZoomed, shouldIgnoreTimelinePanTarget]);

  const handleTimelineTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2) {
      const metrics = getTouchMetrics(e.touches);
      if (!metrics || metrics.centerX === undefined || metrics.distance === undefined) {
        return;
      }

      const touchState = timelineTouchStateRef.current;
      if (!touchState || touchState.mode !== 'pinch') {
        timelineTouchStateRef.current = {
          mode: 'pinch',
          lastCenterX: metrics.centerX,
          lastDistance: Math.max(metrics.distance, 1),
          moved: false,
        };
        return;
      }

      markUserInteracted();
      setCursorSyncEnabled(false);
      const scale = Math.max(touchState.lastDistance || metrics.distance, 1) / Math.max(metrics.distance, 1);
      zoomAroundTime(xToTime(metrics.centerX), scale);

      const deltaX = metrics.centerX - (touchState.lastCenterX ?? metrics.centerX);
      if (Math.abs(deltaX) > 0.5) {
        panByPixels(-deltaX);
        touchState.moved = true;
      }

      touchState.lastCenterX = metrics.centerX;
      touchState.lastDistance = Math.max(metrics.distance, 1);
      return;
    }

    if (e.touches.length !== 1) {
      return;
    }

    const touchState = timelineTouchStateRef.current;
    const metrics = getTouchMetrics(e.touches);
    if (!metrics || !touchState || touchState.mode !== 'pan') {
      return;
    }

    const deltaX = metrics.localX - (touchState.lastX ?? metrics.localX);
    if (Math.abs(deltaX) < 0.5) {
      return;
    }

    markUserInteracted();
    setCursorSyncEnabled(false);
    panByPixels(-deltaX);
    touchState.lastX = metrics.localX;
    touchState.moved = true;
  }, [getTouchMetrics, markUserInteracted, panByPixels, setCursorSyncEnabled, xToTime, zoomAroundTime]);

  const handleTimelineTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const touchState = timelineTouchStateRef.current;
    if (!touchState) {
      return;
    }

    suppressTimelineClickRef.current = touchState.moved;

    if (e.touches.length === 1 && isTimelineZoomed) {
      const metrics = getTouchMetrics(e.touches);
      if (metrics) {
        timelineTouchStateRef.current = {
          mode: 'pan',
          lastX: metrics.localX,
          moved: touchState.moved,
        };
        return;
      }
    }

    if (e.touches.length === 2) {
      const metrics = getTouchMetrics(e.touches);
      if (metrics && metrics.centerX !== undefined && metrics.distance !== undefined) {
        timelineTouchStateRef.current = {
          mode: 'pinch',
          lastCenterX: metrics.centerX,
          lastDistance: Math.max(metrics.distance, 1),
          moved: touchState.moved,
        };
        return;
      }
    }

    timelineTouchStateRef.current = null;
  }, [getTouchMetrics, isTimelineZoomed]);

  const handleZoomIn = useCallback(() => {
    setCursorSyncEnabled(false);
    zoomAroundTime(currentTime, 0.8);
  }, [currentTime, setCursorSyncEnabled, zoomAroundTime]);

  const handleZoomOut = useCallback(() => {
    setCursorSyncEnabled(false);
    zoomAroundTime(currentTime, 1.25);
  }, [currentTime, setCursorSyncEnabled, zoomAroundTime]);

  const handleResetZoom = useCallback(() => {
    resetZoom();
    requestCursorSync();
  }, [requestCursorSync, resetZoom]);

  // Get retention value at a specific time based on selected mode
  const getRetentionAtTime = (time: number): { value: number; label: string; color: string; displayValue: string } | null => {
    if (!retentionData?.retentionData?.length || totalDuration === 0) return null;
    if (retentionMode === 'off') return null;

    const videoDuration = retentionData.videoDurationSeconds || totalDuration;

    // Find the two closest retention data points and interpolate
    let closestBefore: { timestamp: number; relative: number; absolute: number } | null = null;
    let closestAfter: { timestamp: number; relative: number; absolute: number } | null = null;

    for (const point of retentionData.retentionData) {
      const timestamp = point.timestampSeconds !== undefined
        ? point.timestampSeconds
        : point.ratio * videoDuration;

      const pointData = {
        timestamp,
        relative: point.relativeRetention,
        absolute: point.absoluteRetention / 100 // Convert from percentage to 0-1 scale
      };

      if (timestamp <= time) {
        if (!closestBefore || timestamp > closestBefore.timestamp) {
          closestBefore = pointData;
        }
      }
      if (timestamp >= time) {
        if (!closestAfter || timestamp < closestAfter.timestamp) {
          closestAfter = pointData;
        }
      }
    }

    if (!closestBefore && !closestAfter) return null;
    if (!closestBefore) closestBefore = closestAfter;
    if (!closestAfter) closestAfter = closestBefore;

    // Interpolate between the two points
    let relativeValue: number;
    let absoluteValue: number;
    if (closestBefore!.timestamp === closestAfter!.timestamp) {
      relativeValue = closestBefore!.relative;
      absoluteValue = closestBefore!.absolute;
    } else {
      const t = (time - closestBefore!.timestamp) / (closestAfter!.timestamp - closestBefore!.timestamp);
      relativeValue = closestBefore!.relative + t * (closestAfter!.relative - closestBefore!.relative);
      absoluteValue = closestBefore!.absolute + t * (closestAfter!.absolute - closestBefore!.absolute);
    }

    // Calculate adjusted value: weight relative retention by absolute retention
    // This reduces the influence of relative retention when few viewers remain
    // Formula: adjusted = relative * sqrt(absolute) to smooth the weighting
    const adjustedValue = relativeValue * Math.sqrt(absoluteValue);

    // Get value based on mode
    let value: number;
    let displayValue: string;

    // YouTube's baseline is 0.5 for relativeRetentionPerformance
    // Values > 0.5 = above average, < 0.5 = below average
    const youtubeBaseline = 0.5;

    // For relative/adjusted modes, calculate deviation from YouTube's 0.5 baseline
    let deviationFromAvg: number;

    switch (retentionMode) {
      case 'absolute':
        value = absoluteValue;
        displayValue = `${Math.round(absoluteValue * 100)}%`;
        deviationFromAvg = 0; // Not applicable for absolute
        break;
      case 'adjusted': {
        value = adjustedValue;
        // For adjusted: 0.5 × sqrt(mean abs) is the baseline
        const avgAbs = retentionData.retentionData.reduce((sum: number, p: any) => sum + p.absoluteRetention / 100, 0) / retentionData.retentionData.length;
        const adjustedBaseline = youtubeBaseline * Math.sqrt(avgAbs);
        deviationFromAvg = adjustedValue - adjustedBaseline;
        // Show as percentage points above/below baseline
        const deviationPercent = Math.round(deviationFromAvg * 100);
        displayValue = deviationPercent >= 0 ? `+${deviationPercent}%` : `${deviationPercent}%`;
        break;
      }
      case 'drops':
        // For drops mode, show absolute retention (drops are computed from this)
        value = absoluteValue;
        displayValue = `${Math.round(absoluteValue * 100)}% watching`;
        deviationFromAvg = 0;
        break;
      case 'relative':
      default:
        value = relativeValue;
        // Use YouTube's 0.5 baseline
        deviationFromAvg = relativeValue - youtubeBaseline;
        // Show as percentage points above/below baseline (e.g., 0.65 = +15%)
        const devPercent = Math.round(deviationFromAvg * 100);
        displayValue = devPercent >= 0 ? `+${devPercent}%` : `${devPercent}%`;
        break;
    }

    // For relative/adjusted modes: use position relative to 0.5 baseline
    // For absolute/drops: use percentile within video's data
    let label: string;
    let color: string;

    if (retentionMode === 'relative' || retentionMode === 'adjusted') {
      // Traffic light colors based on deviation from YouTube's 0.5 baseline
      // Scale: 0.5 = average, +0.25 = 75%, +0.15 = 65%, etc.
      if (deviationFromAvg >= 0.25) {
        label = 'Very High';
        color = '#22c55e'; // green-500 (75%+)
      } else if (deviationFromAvg >= 0.15) {
        label = 'High';
        color = '#84cc16'; // lime-500 (65%+)
      } else if (deviationFromAvg >= 0.05) {
        label = 'Above Avg';
        color = '#eab308'; // yellow-500 (55%+)
      } else if (deviationFromAvg >= -0.05) {
        label = 'Average';
        color = '#fbbf24'; // amber-400 (45-55%)
      } else if (deviationFromAvg >= -0.15) {
        label = 'Below Avg';
        color = '#f97316'; // orange-500 (35-45%)
      } else {
        label = 'Low';
        color = '#ef4444'; // red-500 (<35%)
      }
    } else {
      // For absolute/drops modes, use percentile within video's data
      const allValues = retentionData.retentionData.map((p: any) => p.absoluteRetention / 100);
      const sortedValues = [...allValues].sort((a, b) => a - b);
      const percentile = sortedValues.filter(v => v <= value).length / sortedValues.length;

      if (percentile >= 0.9) {
        label = 'Very High';
        color = '#22c55e'; // green-500
      } else if (percentile >= 0.75) {
        label = 'High';
        color = '#84cc16'; // lime-500
      } else if (percentile >= 0.5) {
        label = 'Above Avg';
        color = '#eab308'; // yellow-500
      } else if (percentile >= 0.25) {
        label = 'Below Avg';
        color = '#f97316'; // orange-500
      } else if (percentile >= 0.1) {
        label = 'Low';
        color = '#ef4444'; // red-500
      } else {
        label = 'Very Low';
        color = '#dc2626'; // red-600
      }
    }

    return { value, label, color, displayValue };
  };

  // Get viewer drops/gains at a specific time (for tooltip)
  const getDropsAtTime = (time: number): { dropped: number; gained: number } | null => {
    if (!retentionData?.retentionData?.length || totalDuration === 0) return null;

    const videoDuration = retentionData.videoDurationSeconds || totalDuration;
    const totalViews = retentionData.videoStats?.views || 100000;
    const bucketSize = 30; // Same as drops visualization

    const bucketStart = Math.floor(time / bucketSize) * bucketSize;
    const bucketEnd = Math.min(bucketStart + bucketSize, videoDuration);

    // Find retention values at bucket boundaries
    const getAbsRetentionAt = (t: number) => {
      const point = retentionData.retentionData.reduce((closest: any, p: any) => {
        const pTime = p.timestampSeconds !== undefined ? p.timestampSeconds : p.ratio * videoDuration;
        const closestTime = closest.timestampSeconds !== undefined ? closest.timestampSeconds : closest.ratio * videoDuration;
        return Math.abs(pTime - t) < Math.abs(closestTime - t) ? p : closest;
      });
      return point.absoluteRetention / 100;
    };

    const retentionStart = getAbsRetentionAt(bucketStart);
    const retentionEnd = getAbsRetentionAt(bucketEnd);

    const viewersStart = totalViews * retentionStart;
    const viewersEnd = totalViews * retentionEnd;
    const netChange = viewersEnd - viewersStart;

    return {
      dropped: netChange < 0 ? Math.abs(netChange) : 0,
      gained: netChange > 0 ? netChange : 0
    };
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  // Get topic color based on visualization mode and quality
  const getTopicScoreParts = useCallback((topic: Pick<TTMLElement, 'content' | 'rank' | 'maxRank'>) => {
    let score = topic.rank ?? 5;  // Default to 5 if undefined
    let maxScore = topic.maxRank ?? 10;  // Default to 10 if undefined

    if (topicVisualizationMode === 'overall') {
      // Overall score: Score: X/10
      const scoreMatch = topic.content.match(/Score:\s*\[?(\d+)\/(\d+)\]?/);
      if (scoreMatch) {
        score = parseInt(scoreMatch[1]);
        maxScore = parseInt(scoreMatch[2]);
      }
    } else if (topicVisualizationMode === 'substance') {
      // Substance: S: X/3
      const substanceMatch = topic.content.match(/S:\s*(\d+)\/(\d+)/);
      if (substanceMatch) {
        score = parseInt(substanceMatch[1]);
        maxScore = parseInt(substanceMatch[2]);
      }
    } else if (topicVisualizationMode === 'resonance') {
      // Resonance: R: X/2
      const resonanceMatch = topic.content.match(/R:\s*(\d+)\/(\d+)/);
      if (resonanceMatch) {
        score = parseInt(resonanceMatch[1]);
        maxScore = parseInt(resonanceMatch[2]);
      }
    } else if (topicVisualizationMode === 'clarity') {
      // Clarity: C: X/2
      const clarityMatch = topic.content.match(/C:\s*(\d+)\/(\d+)/);
      if (clarityMatch) {
        score = parseInt(clarityMatch[1]);
        maxScore = parseInt(clarityMatch[2]);
      }
    } else if (topicVisualizationMode === 'production') {
      // Production: P: X/2
      const productionMatch = topic.content.match(/P:\s*(\d+)\/(\d+)/);
      if (productionMatch) {
        score = parseInt(productionMatch[1]);
        maxScore = parseInt(productionMatch[2]);
      }
    } else if (topicVisualizationMode === 'creative_risk') {
      // Creative Risk: CR: X/1
      const crMatch = topic.content.match(/CR:\s*(\d+)\/(\d+)/);
      if (crMatch) {
        score = parseInt(crMatch[1]);
        maxScore = parseInt(crMatch[2]);
      }
    }

    return {
      score,
      maxScore,
      ratio: maxScore > 0 ? score / maxScore : 0
    };
  }, [topicVisualizationMode]);

  const getTopicVisualizationLabel = useCallback(() => (
    topicVisualizationMode === 'creative_risk'
      ? 'Creative Risk'
      : topicVisualizationMode.charAt(0).toUpperCase() + topicVisualizationMode.slice(1)
  ), [topicVisualizationMode]);
  const timelineVisualizationKey = `${topicVisualizationMode}-${retentionMode}-${showGradient}-${overlayMode}`;

  // Normalize a raw retention value to a 0-1 ratio suitable for color thresholds.
  // For relative/adjusted: anchored at the baseline (0.5 for relative).
  //   - At or above baseline → 0.4 to 1.0 (yellow to green, never red)
  //   - Below baseline → 0.0 to 0.4 (red to yellow)
  // For absolute: uses the video's own min/max range.
  const normalizeRetentionForColor = useCallback((rawValue: number): number => {
    if (!retentionData?.retentionData?.length) return clamp(rawValue, 0, 1);

    const points = retentionData.retentionData;

    if (retentionMode === 'relative') {
      // Baseline is 0.5. Above = yellow→green, below = red→yellow.
      const baseline = 0.5;
      const allValues = points.map((p: any) => p.relativeRetention ?? 0.5);
      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);

      if (rawValue >= baseline) {
        // Above average: map [baseline, maxVal] → [0.4, 1.0]
        const aboveRange = maxVal - baseline;
        if (aboveRange > 0.001) {
          return 0.4 + 0.6 * ((rawValue - baseline) / aboveRange);
        }
        return 0.5; // all values equal to baseline
      } else {
        // Below average: map [minVal, baseline] → [0.0, 0.4]
        const belowRange = baseline - minVal;
        if (belowRange > 0.001) {
          return 0.4 * ((rawValue - minVal) / belowRange);
        }
        return 0.3;
      }
    } else if (retentionMode === 'adjusted') {
      // Same asymmetric approach, baseline = 0.5 * sqrt(avgAbsolute)
      const avgAbs = points.reduce((sum: number, p: any) => sum + (p.absoluteRetention ?? 50) / 100, 0) / points.length;
      const baseline = 0.5 * Math.sqrt(avgAbs);
      const allValues = points.map((p: any) => {
        const rel = p.relativeRetention ?? 0.5;
        const abs = (p.absoluteRetention ?? 50) / 100;
        return rel * Math.sqrt(abs);
      });
      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);

      if (rawValue >= baseline) {
        const aboveRange = maxVal - baseline;
        if (aboveRange > 0.001) return 0.4 + 0.6 * ((rawValue - baseline) / aboveRange);
        return 0.5;
      } else {
        const belowRange = baseline - minVal;
        if (belowRange > 0.001) return 0.4 * ((rawValue - minVal) / belowRange);
        return 0.3;
      }
    } else {
      // Absolute: use video's own data range (symmetric — no baseline concept)
      const allValues = points.map((p: any) => (p.absoluteRetention ?? 50) / 100);
      const minVal = Math.min(...allValues);
      const maxVal = Math.max(...allValues);
      const range = maxVal - minVal;
      if (range > 0.001) return clamp((rawValue - minVal) / range, 0, 1);
      return 0.5;
    }
  }, [retentionMode, retentionData]);

  const getTopicColor = (topic: TTMLElement): string => {
    if (retentionMode !== 'off' && retentionMode !== 'drops' && retentionData?.retentionData?.length) {
      const midpoint = (topic.start + topic.end) / 2;
      const retention = getRetentionAtTime(midpoint);
      if (retention) {
        const ratio = normalizeRetentionForColor(retention.value);
        if (showGradient) {
          if (ratio >= 0.8) return 'rgb(34, 197, 94)';
          if (ratio >= 0.6) return 'rgb(132, 204, 22)';
          if (ratio >= 0.4) return 'rgb(250, 204, 21)';
          if (ratio >= 0.2) return 'rgb(251, 146, 60)';
          return 'rgb(239, 68, 68)';
        }
        if (ratio >= 0.67) return 'rgb(34, 197, 94)';
        if (ratio >= 0.33) return 'rgb(234, 179, 8)';
        return 'rgb(239, 68, 68)';
      }
    }

    const { ratio } = getTopicScoreParts(topic);

    if (showGradient) {
      // Gradient from red to yellow to green
      if (ratio >= 0.8) return 'rgb(34, 197, 94)';   // green-500
      if (ratio >= 0.6) return 'rgb(132, 204, 22)';  // lime-500
      if (ratio >= 0.4) return 'rgb(250, 204, 21)';  // yellow-400
      if (ratio >= 0.2) return 'rgb(251, 146, 60)';  // orange-400
      return 'rgb(239, 68, 68)';  // red-500
    } else {
      // Simple three-color scheme
      if (ratio >= 0.67) return 'rgb(34, 197, 94)';  // green-500
      if (ratio >= 0.33) return 'rgb(234, 179, 8)';  // yellow-500
      return 'rgb(239, 68, 68)';  // red-500
    }
  };

  // Get topic gradient
  const getTopicGradient = (topic: TTMLElement): string => {
    const color = getTopicColor(topic);
    // Parse rgb values
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      const [, r, g, b] = match;
      return `linear-gradient(to right, rgba(${r}, ${g}, ${b}, 0.8), rgba(${r}, ${g}, ${b}, 1), rgba(${r}, ${g}, ${b}, 0.8))`;
    }
    return color;
  };

  const canUseStructuredTtmlApi = !!(sessionId && token && !effectiveIsPublicView);
  const shouldPreferStructuredTtmlData = canUseStructuredTtmlApi && !!lastLoadedChecksum && !structuredLoadFailed;

  useEffect(() => {
    setStructuredLoadFailed(false);
  }, [lastLoadedChecksum, selectedTtmlId]);

  // Auto-select TTML file based on session selection or most recent
  useEffect(() => {
    console.log('[AUDIO-PLAYER] Auto-select effect running:', {
      ttmlFilesCount: ttmlFiles?.length || 0,
      selectedTtmlId: selectedTtmlId?.substring(0, 8),
      currentSelectedTtmlFile: selectedTtmlFile,
      currentLastLoadedChecksum: lastLoadedChecksum?.substring(0, 8),
      availableChecksums: ttmlFiles?.map(f => f.checksum?.substring(0, 8)).join(','),
      filesWithContent: ttmlFiles?.filter(f => !!f.content).length || 0
    });

    if (ttmlFiles && ttmlFiles.length > 0) {
      // If selectedTtmlId is provided (including preview), always use it
      if (selectedTtmlId) {
        console.log('[AUDIO-PLAYER] Looking for file with selectedTtmlId:', selectedTtmlId.substring(0, 8));

        const selectedFile = ttmlFiles.find(file =>
          file.checksum === selectedTtmlId ||
          (file.filename || file.name) === selectedTtmlId
        );

        if (selectedFile) {
          const newFileName = selectedFile.filename || selectedFile.name;
          const newChecksum = selectedFile.checksum || selectedTtmlId;
          console.log('[AUDIO-PLAYER] Found file:', { newFileName, newChecksum: newChecksum?.substring(0, 8), hasContent: !!selectedFile.content });

          // Always update state and content when selectedTtmlId changes
          setSelectedTtmlFile(newFileName);
          setLastLoadedChecksum(newChecksum);

          // If the file has content, set it directly
          if (selectedFile.content) {
            console.log('[AUDIO-PLAYER] Setting content directly, length:', selectedFile.content.length);
            setCurrentTtmlContent(selectedFile.content);
          }
          return;
        } else {
          console.log('[AUDIO-PLAYER] File NOT found in ttmlFiles for selectedTtmlId:', selectedTtmlId.substring(0, 8));
          // File not in ttmlFiles - the direct preview fetch effect will handle it
        }
      }

      // If no selectedTtmlId, use the first file (most reliable - matches ReadableTranscriptTab behavior)
      if (!selectedTtmlId) {
        const firstFile = ttmlFiles[0];
        if (firstFile) {
          const fileName = firstFile.filename || firstFile.name;
          const checksum = firstFile.checksum || null;
          console.log('[AUDIO-PLAYER] Auto-selecting first file:', fileName, 'checksum:', checksum?.substring(0, 8), 'hasContent:', !!firstFile.content);
          setSelectedTtmlFile(fileName);
          setLastLoadedChecksum(checksum);

          // If the file has content, also set it directly
          if (firstFile.content) {
            console.log('[AUDIO-PLAYER] File has content, setting directly, length:', firstFile.content.length);
            setCurrentTtmlContent(firstFile.content);
          }
        }
      }
    }
  }, [ttmlFiles, selectedTtmlId]); // Always re-run when these change

  // Ensure content is loaded when ttmlFiles update with content (fallback for race conditions)
  useEffect(() => {
    if (shouldPreferStructuredTtmlData) return;
    // Only run if we have files, a selection, but no current content
    if (!ttmlFiles || ttmlFiles.length === 0) return;
    if (!selectedTtmlFile && !lastLoadedChecksum) return;
    if (currentTtmlContent) return; // Already have content

    console.log('[AUDIO-PLAYER] Fallback content load check:', {
      selectedTtmlFile,
      lastLoadedChecksum: lastLoadedChecksum?.substring(0, 8),
      hasCurrentContent: !!currentTtmlContent
    });

    // Try to find the selected file and load its content
    const selectedFile = lastLoadedChecksum
      ? ttmlFiles.find(f => f.checksum === lastLoadedChecksum)
      : ttmlFiles.find(f => (f.filename || f.name) === selectedTtmlFile);

    if (selectedFile?.content) {
      console.log('[AUDIO-PLAYER] Fallback: Found file with content, loading it, length:', selectedFile.content.length);
      setCurrentTtmlContent(selectedFile.content);
    }
  }, [ttmlFiles, selectedTtmlFile, lastLoadedChecksum, currentTtmlContent, shouldPreferStructuredTtmlData]);

  // Direct preview fetch - when selectedTtmlId is a checksum but file not in ttmlFiles
  useEffect(() => {
    if (shouldPreferStructuredTtmlData) return;
    if (!selectedTtmlId) return;

    // Check if we already have this file in ttmlFiles WITH CONTENT
    const existsInFiles = ttmlFiles?.find(file =>
      file.checksum === selectedTtmlId ||
      (file.filename || file.name) === selectedTtmlId
    );

    // If file exists in ttmlFiles WITH content, the other useEffect will handle it
    // But if it exists without content, we should still fetch directly
    if (existsInFiles && existsInFiles.content) {
      console.log('[AUDIO-PLAYER] File exists in ttmlFiles with content, skipping direct fetch');
      return;
    }

    if (effectiveIsPublicView && !existsInFiles) {
      console.log('[AUDIO-PLAYER] Public share has no matching TTML metadata, skipping direct preview fetch');
      return;
    }

    // Otherwise, fetch directly by checksum (for preview hover)
    const fetchPreviewContent = async () => {
      console.log('[AUDIO-PLAYER] Fetching preview for checksum:', selectedTtmlId);
      try {
        const content = await fetchTtmlContent(selectedTtmlId);
        if (content) {
          console.log('[AUDIO-PLAYER] Loaded preview TTML content, length:', content.length);
          setCurrentTtmlContent(content);
        }
      } catch (error) {
        console.error('[AUDIO-PLAYER] Failed to fetch preview TTML:', error);
      }
    };

    fetchPreviewContent();
  }, [selectedTtmlId, ttmlFiles, effectiveIsPublicView, fetchTtmlContent, shouldPreferStructuredTtmlData]);

  // Load selected TTML file content
  useEffect(() => {
    if (shouldPreferStructuredTtmlData) return;
    console.log('[AUDIO-PLAYER] Load content effect:', {
      selectedTtmlFile,
      lastLoadedChecksum: lastLoadedChecksum?.substring(0, 8),
      ttmlFilesCount: ttmlFiles?.length || 0,
      hasToken: !!token
    });

    if (!selectedTtmlFile || !ttmlFiles) {
      console.log('[AUDIO-PLAYER] Load content effect returning early - missing selectedTtmlFile or ttmlFiles');
      return;
    }

    const loadTtmlContent = async () => {
      // Find by checksum first (for version-specific loading), then fall back to filename
      let selectedFile = lastLoadedChecksum
        ? ttmlFiles.find(file => file.checksum === lastLoadedChecksum)
        : null;

      console.log('[AUDIO-PLAYER] Looking for file by checksum:', lastLoadedChecksum?.substring(0, 8), 'found:', !!selectedFile);

      // Fall back to finding by filename if checksum not found
      if (!selectedFile) {
        selectedFile = ttmlFiles.find(file =>
          (file.filename || file.name) === selectedTtmlFile
        );
        console.log('[AUDIO-PLAYER] Looking for file by filename:', selectedTtmlFile, 'found:', !!selectedFile);
      }

      if (!selectedFile) {
        console.log('[AUDIO-PLAYER] No file found, returning');
        return;
      }

      console.log('[AUDIO-PLAYER] Found file to load:', {
        name: selectedFile.name || selectedFile.filename,
        checksum: selectedFile.checksum?.substring(0, 8),
        hasContent: !!selectedFile.content,
        contentLength: selectedFile.content?.length || 0
      });

      try {
        let ttmlContent = selectedFile.content;

        if (!ttmlContent && selectedFile.checksum) {
          console.log('[AUDIO-PLAYER] Loading TTML content for checksum:', selectedFile.checksum?.substring(0, 8));
          ttmlContent = await fetchTtmlContent(selectedFile.checksum) || undefined;
        }

        if (ttmlContent) {
          console.log('[AUDIO-PLAYER] Setting currentTtmlContent, length:', ttmlContent.length, 'starts with:', ttmlContent.substring(0, 50));
          setCurrentTtmlContent(ttmlContent);
        } else {
          console.log('[AUDIO-PLAYER] No ttmlContent to set!');
        }
      } catch (error) {
        console.error('Error loading TTML content:', error);
      }
    };

    loadTtmlContent();
  }, [selectedTtmlFile, ttmlFiles, fetchTtmlContent, lastLoadedChecksum, shouldPreferStructuredTtmlData]);

  useEffect(() => {
    if (!canUseStructuredTtmlApi || !lastLoadedChecksum || !sessionId) {
      return;
    }

    let cancelled = false;

    const loadStructuredElements = async () => {
      try {
        const structure = effectiveIsPublicView && effectiveShareToken
          ? await TtmlMetadataService.getPublicStructure(effectiveShareToken, lastLoadedChecksum)
          : await TtmlMetadataService.getStructure(sessionId, lastLoadedChecksum, token);
        if (cancelled) return;

        setTtmlElements({
          clips: structure.clips.map(mapStructuredClipToTimelineElement),
          topics: structure.topics.map(mapStructuredTopicToTimelineElement),
          edits: structure.edits.map(mapStructuredEditToTimelineElement),
          references: structure.references.map(mapStructuredReferenceToTimelineElement),
        });
        setStructuredLoadFailed(false);
      } catch (error) {
        console.error('[AUDIO-PLAYER] Failed to load structured TTML metadata:', error);
        if (!cancelled) {
          setStructuredLoadFailed(true);
        }
      }
    };

    loadStructuredElements();

    return () => {
      cancelled = true;
    };
  }, [canUseStructuredTtmlApi, effectiveIsPublicView, effectiveShareToken, lastLoadedChecksum, sessionId, token]);

  // Parse TTML content when it changes
  useEffect(() => {
    if (shouldPreferStructuredTtmlData) {
      return;
    }
    console.log('[AUDIO-PLAYER] Parse effect running, currentTtmlContent length:', currentTtmlContent?.length || 0);

    if (!currentTtmlContent) {
      console.log('[AUDIO-PLAYER] No currentTtmlContent, clearing elements');
      setTtmlElements({ clips: [], topics: [], edits: [], references: [] });
      return;
    }

    const parseElements = () => {
      console.log('[AUDIO-PLAYER] parseElements() called');
      const allClips: TTMLElement[] = [];
      const allTopics: TTMLElement[] = [];
      const allEdits: TTMLElement[] = [];
      const allReferences: TTMLElement[] = [];

      try {
        console.log('[AUDIO-PLAYER] Starting XML parse, content preview:', currentTtmlContent.substring(0, 200));

        // Pre-process: Convert HTML named entities to numeric entities for XML compatibility
        // XML only supports &lt; &gt; &amp; &apos; &quot; by default
        const htmlEntityMap: { [key: string]: string } = {
          '&auml;': '&#228;',  // ä
          '&ouml;': '&#246;',  // ö
          '&uuml;': '&#252;',  // ü
          '&Auml;': '&#196;',  // Ä
          '&Ouml;': '&#214;',  // Ö
          '&Uuml;': '&#220;',  // Ü
          '&szlig;': '&#223;', // ß
          '&nbsp;': '&#160;',  // non-breaking space
          '&copy;': '&#169;',  // ©
          '&reg;': '&#174;',   // ®
          '&trade;': '&#8482;', // ™
          '&ndash;': '&#8211;', // –
          '&mdash;': '&#8212;', // —
          '&lsquo;': '&#8216;', // '
          '&rsquo;': '&#8217;', // '
          '&ldquo;': '&#8220;', // "
          '&rdquo;': '&#8221;', // "
          '&bull;': '&#8226;',  // •
          '&hellip;': '&#8230;', // …
          '&eacute;': '&#233;', // é
          '&egrave;': '&#232;', // è
          '&aacute;': '&#225;', // á
          '&agrave;': '&#224;', // à
          '&iacute;': '&#237;', // í
          '&oacute;': '&#243;', // ó
          '&uacute;': '&#250;', // ú
          '&ntilde;': '&#241;', // ñ
          '&ccedil;': '&#231;', // ç
        };

        let xmlContent = currentTtmlContent;
        for (const [entity, numeric] of Object.entries(htmlEntityMap)) {
          xmlContent = xmlContent.split(entity).join(numeric);
        }

        // Fallback: Convert any remaining HTML named entities to a placeholder or remove them
        // This regex matches &word; patterns (HTML named entities) that aren't the 5 valid XML entities
        xmlContent = xmlContent.replace(/&(?!amp;|lt;|gt;|apos;|quot;|#\d+;|#x[0-9a-fA-F]+;)([a-zA-Z]+);/g, (match, entityName) => {
          console.warn('[AUDIO-PLAYER] Unknown HTML entity encountered:', match);
          // Return empty string to remove unknown entities, or you could return the entity name
          return '';
        });

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, 'application/xml');
        console.log('[AUDIO-PLAYER] XML parsed, document element:', xmlDoc.documentElement?.tagName);

        // Check for parse errors - check in multiple ways
        const parseError = xmlDoc.querySelector('parsererror');
        const parseErrorNS = xmlDoc.getElementsByTagName('parsererror');
        console.log('[AUDIO-PLAYER] Parse error check - querySelector:', !!parseError, ', getElementsByTagName:', parseErrorNS.length);

        if (parseError) {
          console.error('[AUDIO-PLAYER] XML parse error:', parseError.textContent);
          return;
        }
        if (parseErrorNS.length > 0) {
          console.error('[AUDIO-PLAYER] XML parse error (NS check):', parseErrorNS[0].textContent);
          return;
        }

        // Parse time to seconds
        const parseTimeToSeconds = (timeString: string): number => {
          const parts = timeString.split(':');
          if (parts.length === 3) {
            const hours = parseInt(parts[0]);
            const minutes = parseInt(parts[1]);
            const seconds = parseFloat(parts[2]);
            return hours * 3600 + minutes * 60 + seconds;
          }
          return 0;
        };

        // Helper to get elements with namespace fallback
        const getElements = (parent: Element | Document, tagName: string): Element[] => {
          let elements = parent.querySelectorAll(tagName);
          if (elements.length === 0) {
            // Try with namespace
            const nsElements = parent.getElementsByTagNameNS('*', tagName);
            return Array.from(nsElements);
          }
          return Array.from(elements);
        };

        // Helper to filter elements with begin/end attributes
        const getTimedElements = (parent: Element, tagName: string): Element[] => {
          const elements = getElements(parent, tagName);
          return elements.filter(el => el.getAttribute('begin') && el.getAttribute('end'));
        };

        // Find divs by iterating through all divs (handle namespace issues)
        const allDivs = getElements(xmlDoc, 'div');

        console.log('[AUDIO-PLAYER] Found', allDivs.length, 'div elements');

        allDivs.forEach(div => {
          const xmlId = div.getAttribute('xml:id');
          console.log('[AUDIO-PLAYER] Processing div with xml:id:', xmlId);

          if (xmlId === 'd_clip') {
            // Extract clips
            const clipElements = getTimedElements(div, 'p');
            clipElements.forEach((element) => {
              const timeBegin = element.getAttribute('begin');
              const timeEnd = element.getAttribute('end');
              let content = element.textContent || '';
              
              // Decode HTML entities
              content = content
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&apos;/g, "'");

              if (timeBegin && timeEnd && !content.includes('PLACEHOLDER')) {
                allClips.push({
                  id: `clip_${allClips.length + 1}`,
                  start: parseTimeToSeconds(timeBegin),
                  end: parseTimeToSeconds(timeEnd),
                  type: 'clip',
                  content: content.trim()
                });
              }
            });
          } else if (xmlId === 'd_summary') {
            // Extract topics
            const topicElements = getTimedElements(div, 'p');
            topicElements.forEach((element) => {
              const timeBegin = element.getAttribute('begin');
              const timeEnd = element.getAttribute('end');
              let content = element.textContent || '';
              
              // Decode HTML entities
              content = content
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&apos;/g, "'");

              if (timeBegin && timeEnd) {
                // Extract title and ranking from content - handles [1: 20/20] and [1: ?/9] formats
                const segmentMatch = content.match(/\[(\d+):\s*(\d+|\?)\/(\d+)\]\s*(?:>>)\s*\[([^\]]+)\]/);
                let rank = 5;
                let maxRank = 10;
                let title = content.substring(0, 50);
                let segmentNumber = allTopics.length + 1; // Default to array index

                if (segmentMatch) {
                  // segmentMatch[1] is the original segment number from the TTML
                  segmentNumber = parseInt(segmentMatch[1], 10);
                  // segmentMatch[2] and [3] are position values, not quality scores (may be '?' for unrated)
                  const positionRank = parseInt(segmentMatch[2], 10) || 0;
                  const positionTotal = parseInt(segmentMatch[3], 10) || 0;
                  title = segmentMatch[4].trim();

                  // Extract the actual score from content - also handle brackets
                  const scoreMatch = content.match(/Score:\s*\[?(\d+)\/(\d+)\]?/);
                  if (scoreMatch) {
                    rank = parseInt(scoreMatch[1], 10);
                    maxRank = parseInt(scoreMatch[2], 10);
                  } else {
                    // Default values if no score found
                    rank = 5;
                    maxRank = 10;
                    console.warn('[CentralAudioPlayer] No score found for topic:', title);
                  }
                } else {
                  // No segment match found, try to extract title differently
                  const titleMatch = content.match(/^([^:]+):/)?.[1];
                  if (titleMatch) title = titleMatch.trim();

                  // Still try to find score
                  const scoreMatch = content.match(/Score:\s*\[?(\d+)\/(\d+)\]?/);
                  if (scoreMatch) {
                    rank = parseInt(scoreMatch[1], 10);
                    maxRank = parseInt(scoreMatch[2], 10);
                  } else {
                    rank = 5;
                    maxRank = 10;
                  }
                  console.warn('[CentralAudioPlayer] No segment pattern found for topic:', title);
                }

                // Validate rank values before pushing
                const validRank = isNaN(rank) || rank < 0 ? 5 : rank;
                const validMaxRank = isNaN(maxRank) || maxRank <= 0 ? 10 : maxRank;

                allTopics.push({
                  id: `topic_${allTopics.length + 1}`,
                  start: parseTimeToSeconds(timeBegin),
                  end: parseTimeToSeconds(timeEnd),
                  type: 'topic',
                  content: content.trim(),
                  title: title,
                  rank: validRank,
                  maxRank: validMaxRank,
                  segmentNumber: segmentNumber // Store original segment number
                });
              }
            });
          } else if (xmlId === 'd_edit') {
            // Extract edits
            const editElements = getTimedElements(div, 'p');
            editElements.forEach((element) => {
              const timeBegin = element.getAttribute('begin');
              const timeEnd = element.getAttribute('end');
              const content = element.textContent || '';

              if (timeBegin && timeEnd) {
                allEdits.push({
                  id: `edit_${allEdits.length + 1}`,
                  start: parseTimeToSeconds(timeBegin),
                  end: parseTimeToSeconds(timeEnd),
                  type: 'edit',
                  content: content.trim()
                });
              }
            });
          } else if (xmlId === 'd_references') {
            // Extract references
            const refElements = getTimedElements(div, 'p');
            refElements.forEach((element) => {
              const timeBegin = element.getAttribute('begin');
              const timeEnd = element.getAttribute('end');

              // Extract lines by iterating child nodes - br elements separate lines
              const lines: string[] = [];
              let currentLine = '';

              element.childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                  currentLine += node.textContent || '';
                } else if (node.nodeName.toLowerCase() === 'br') {
                  lines.push(currentLine.trim());
                  currentLine = '';
                }
              });
              // Don't forget the last line
              if (currentLine.trim()) {
                lines.push(currentLine.trim());
              }

              // Decode HTML entities in each line
              const decodedLines = lines.map(line =>
                line
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'")
                  .replace(/&apos;/g, "'")
              );

              const headerLine = decodedLines[0] || '';

              // Debug logging
              console.log('[REF-PARSE] headerLine:', headerLine);
              console.log('[REF-PARSE] decodedLines:', decodedLines);

              if (timeBegin && timeEnd) {
                // Parse new format: [REF] Name | Link Text | URL<br/>Category<br/>Context<br/>Visual
                // Or old format: [REF] Name (no pipes)

                // Match the reference format: [REF] Name | Link Text | URL
                // Or: [REF] Name (no pipes, no URL)
                const refMatch = headerLine.match(/\[REF\]\s*([^|\n]+?)(?:\s*\|\s*([^|\n]+?)\s*\|\s*(https?:\/\/[^\s<>\n]+))?$/);

                console.log('[REF-PARSE] refMatch:', refMatch);

                let title = headerLine.substring(0, 50);
                let url: string | undefined;
                let linkText: string | undefined;
                let category: string | undefined;
                let context: string | undefined;
                let visualSuggestion: string | undefined;

                if (refMatch) {
                  title = refMatch[1].trim();
                  linkText = refMatch[2]?.trim();
                  url = refMatch[3]?.trim();
                  console.log('[REF-PARSE] Extracted - title:', title, 'linkText:', linkText, 'url:', url);
                } else {
                  console.log('[REF-PARSE] NO MATCH for headerLine:', headerLine);
                }

                // Parse additional metadata lines (category, context, visual) from remaining decodedLines
                if (decodedLines.length > 1 && decodedLines[1]) {
                  category = decodedLines[1];
                }
                if (decodedLines.length > 2 && decodedLines[2]) {
                  context = decodedLines[2];
                }
                if (decodedLines.length > 3 && decodedLines[3]) {
                  visualSuggestion = decodedLines[3];
                }

                // Clean content for storage (join lines back)
                const cleanContent = decodedLines.join('\n');

                allReferences.push({
                  id: `ref_${allReferences.length + 1}`,
                  start: parseTimeToSeconds(timeBegin),
                  end: parseTimeToSeconds(timeEnd),
                  type: 'reference',
                  content: cleanContent,
                  title: title,
                  url,
                  linkText,
                  category,
                  context,
                  visualSuggestion
                });
              }
            });
          }
        });

        console.log('[CentralAudioPlayer] Parsed TTML elements:', {
          clips: allClips.length,
          topics: allTopics.length,
          edits: allEdits.length,
          references: allReferences.length
        });

        setTtmlElements({ clips: allClips, topics: allTopics, edits: allEdits, references: allReferences });
        
        // Log topic parsing details for debugging
        if (allTopics.length > 0) {
          console.log('[CentralAudioPlayer] Topic parsing details:', {
            totalTopics: allTopics.length,
            sampleTopics: allTopics.slice(0, 3).map(t => ({
              title: t.title,
              rank: t.rank,
              maxRank: t.maxRank,
              hasRank: t.rank !== undefined,
              hasMaxRank: t.maxRank !== undefined
            }))
          });
        }
      } catch (error) {
        console.error('Error parsing TTML content:', error);
      }
    };

    parseElements();
  }, [currentTtmlContent, shouldPreferStructuredTtmlData]);

  // Volume control
  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!sessionId || !token) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  }, [sessionId, token]);

  // Handle adding comment at specific time
  const handleAddComment = useCallback(async (content: string, timestampSeconds: number) => {
    if (!sessionId) return;
    
    // Just open the comments panel with the pending timestamp and content
    setPendingCommentTime(timestampSeconds);
    setShowCommentsPanel(true);
    setSelectedCommentId(null);
    
    // The Comments component will handle the actual posting
  }, [sessionId]);

  // Handle selecting a comment from timeline
  const handleSelectComment = useCallback((commentId: string) => {
    setSelectedCommentId(commentId);
    setShowCommentsPanel(true);
    setPendingCommentTime(null);
  }, []);

  // Handle add comment button click from playhead
  const handleAddCommentAtPlayhead = useCallback(() => {
    setPendingCommentTime(currentTime);
    setShowCommentsPanel(true);
    setSelectedCommentId(null);
  }, [currentTime]);

  // Handle comments change - only update if actually changed
  const handleCommentsChange = useCallback((newComments: any[]) => {
    console.log('[CentralAudioPlayer] handleCommentsChange called with', newComments.length, 'comments');
    setComments(prev => {
      // Only update if the comments have actually changed
      if (JSON.stringify(prev) === JSON.stringify(newComments)) {
        console.log('[CentralAudioPlayer] Comments unchanged, skipping update');
        return prev;
      }
      console.log('[CentralAudioPlayer] Updating comments from', prev.length, 'to', newComments.length);
      return newComments;
    });
  }, []);
  
  // Handle closing comments panel
  const handleCloseComments = useCallback(() => {
    setShowCommentsPanel(false);
    setPendingCommentTime(null);
    setSelectedCommentId(null);
  }, []);

  // Handle opening the add reference modal
  const handleOpenAddReferenceModal = useCallback((timestamp: number) => {
    setAddReferenceTimestamp(timestamp);
    setShowAddReferenceModal(true);
  }, []);

  const requireStructuredMutationContext = useCallback(() => {
    if (!lastLoadedChecksum || !token || !sessionId) {
      throw new Error('Missing required data to edit metadata');
    }

    return {
      checksum: lastLoadedChecksum,
      authToken: token,
      currentSessionId: sessionId,
    };
  }, [lastLoadedChecksum, sessionId, token]);

  const publishStructuredRevision = useCallback((result: StructuredMutationResponse, revisionNotes: string | null) => {
    setStructuredLoadFailed(false);

    if (result.checksum) {
      setLastLoadedChecksum(result.checksum);
    }

    if (onTtmlRevisionCreated) {
      onTtmlRevisionCreated({
        checksum: result.checksum,
        version: result.version,
        parentChecksum: result.parentChecksum,
        rootChecksum: result.rootChecksum,
        generatedAt: new Date().toISOString(),
        model: 'manual-edit',
        revisionNotes,
        editedBy: 'manual-editor',
      });
    }
  }, [onTtmlRevisionCreated]);

  const handleSaveReference = useCallback(async (reference: NewReference) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    const revisionNotes = `# Revision Notes\n\n## Summary\nManually added reference "${reference.name}" at ${formatTime(reference.startTime)}.\n\n## Changes Made\n- Added new reference: "${reference.name}"${reference.category ? ` (${reference.category})` : ''}${reference.url ? `\n- URL: ${reference.url}` : ''}\n\n## Timestamp\n- Position: ${formatTime(reference.startTime)}`;

    const result = await TtmlMetadataService.mutateReferences(
      currentSessionId,
      checksum,
      [{
        op: 'add',
        data: {
          startTime: reference.startTime,
          endTime: reference.endTime,
          name: reference.name,
          linkText: reference.linkText,
          url: reference.url,
          category: reference.category,
          context: reference.context,
          visual: reference.visualSuggestion,
        },
      }],
      revisionNotes,
      authToken,
    );

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleUpdateReference = useCallback(async (reference: TTMLElement, updates: ReferenceUpdates) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (reference.index === undefined) {
      throw new Error('Reference index is missing');
    }

    const changes: string[] = [];
    if (updates.name !== reference.title) changes.push(`Name: "${reference.title}" -> "${updates.name}"`);
    if (updates.category !== reference.category) changes.push(`Category updated`);
    if (updates.url !== reference.url) changes.push('URL updated');
    if (updates.linkText !== reference.linkText) changes.push('Link text updated');
    if (updates.context !== reference.context) changes.push('Context updated');
    if (updates.visualSuggestion !== reference.visualSuggestion) changes.push('Visual suggestion updated');

    const revisionNotes = `# Revision Notes\n\n## Summary\nManually edited reference "${updates.name}" at ${formatTime(reference.start)}.\n\n## Changes Made\n${changes.length > 0 ? changes.map((change) => `- ${change}`).join('\n') : '- Minor edits'}\n\n## Timestamp\n- Position: ${formatTime(reference.start)}`;

    const result = await TtmlMetadataService.mutateReferences(
      currentSessionId,
      checksum,
      [{
        op: 'update',
        index: reference.index,
        data: {
          name: updates.name,
          linkText: updates.linkText,
          url: updates.url,
          category: updates.category,
          context: updates.context,
          visual: updates.visualSuggestion,
        },
      }],
      revisionNotes,
      authToken,
    );

    setSelectedReference((previous) => previous ? {
      ...previous,
      title: updates.name,
      linkText: updates.linkText,
      url: updates.url,
      category: updates.category,
      context: updates.context,
      visualSuggestion: updates.visualSuggestion,
    } : previous);

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleDeleteReference = useCallback(async (reference: TTMLElement) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (reference.index === undefined) {
      throw new Error('Reference index is missing');
    }

    const revisionNotes = `# Revision Notes\n\n## Summary\nManually deleted reference "${reference.title}" at ${formatTime(reference.start)}.\n\n## Changes Made\n- Deleted reference\n\n## Timestamp\n- Position: ${formatTime(reference.start)}`;

    const result = await TtmlMetadataService.mutateReferences(
      currentSessionId,
      checksum,
      [{ op: 'remove', index: reference.index }],
      revisionNotes,
      authToken,
    );

    setSelectedReference(null);
    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleUpdateReferenceTimestamp = useCallback(async (reference: TimelineLooseElement, newStart: number) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (reference.index === undefined) {
      throw new Error('Reference index is missing');
    }

    const originalStart = (reference as any)._originalStart ?? reference.start;
    const originalEnd = (reference as any)._originalEnd ?? reference.end;
    const duration = originalEnd - originalStart;
    const revisionNotes = `# Revision Notes\n\n## Summary\nMoved reference "${reference.title}" from ${formatTime(originalStart)} to ${formatTime(newStart)}.\n\n## Changes Made\n- Timestamp: ${formatTime(originalStart)} -> ${formatTime(newStart)}\n\n## Method\n- Dragged on timeline`;

    const result = await TtmlMetadataService.mutateReferences(
      currentSessionId,
      checksum,
      [{
        op: 'update',
        index: reference.index,
        data: {
          startTime: newStart,
          endTime: newStart + duration,
        },
      }],
      revisionNotes,
      authToken,
    );

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleReferenceDragStart = useCallback((reference: TimelineLooseElement) => {
    console.log('[CentralAudioPlayer] Reference drag started:', reference.title);
  }, []);

  const handleReferenceDragMove = useCallback((_reference: TimelineLooseElement, newTime: number) => {
    setDragHighlightTime(newTime);
  }, [setDragHighlightTime]);

  const handleReferenceDragEnd = useCallback(async (reference: TimelineLooseElement, newTime: number) => {
    setDragHighlightTime(null);
    const originalStart = (reference as any)._originalStart ?? reference.start;

    if (Math.abs(newTime - originalStart) < 0.5) {
      return;
    }

    await handleUpdateReferenceTimestamp(reference, newTime);
  }, [handleUpdateReferenceTimestamp, setDragHighlightTime]);

  const handleUpdateClip = useCallback(async (clip: TTMLElement, updates: ClipEditUpdates) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (clip.index === undefined) {
      throw new Error('Clip index is missing');
    }

    const revisionNotes = `# Revision Notes\n\n## Summary\nManually edited clip "${updates.title}" at ${formatTime(updates.start)}.\n\n## Changes Made\n- Updated clip content and/or timing\n\n## Timestamp\n- Position: ${formatTime(updates.start)} - ${formatTime(updates.end)}`;

    const result = await TtmlMetadataService.mutateClips(
      currentSessionId,
      checksum,
      [{
        op: 'update',
        index: clip.index,
        data: {
          title: updates.title,
          clipType: updates.clipType,
          description: updates.description,
          visual: updates.visual,
          startTime: updates.start,
          endTime: updates.end,
        },
      }],
      revisionNotes,
      authToken,
    );

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleDeleteClip = useCallback(async (clip: TTMLElement) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (clip.index === undefined) {
      throw new Error('Clip index is missing');
    }

    const revisionNotes = `# Revision Notes\n\n## Summary\nManually deleted clip "${clip.title}" at ${formatTime(clip.start)}.\n\n## Changes Made\n- Deleted clip\n\n## Timestamp\n- Position: ${formatTime(clip.start)}`;

    const result = await TtmlMetadataService.mutateClips(
      currentSessionId,
      checksum,
      [{ op: 'remove', index: clip.index }],
      revisionNotes,
      authToken,
    );

    setSelectedClipEdit(null);
    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleUpdateEdit = useCallback(async (edit: TTMLElement, updates: ClipEditUpdates) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (edit.index === undefined) {
      throw new Error('Edit index is missing');
    }

    const revisionNotes = `# Revision Notes\n\n## Summary\nManually edited edit suggestion "${updates.title}" at ${formatTime(updates.start)}.\n\n## Changes Made\n- Updated edit content and/or timing\n\n## Timestamp\n- Position: ${formatTime(updates.start)} - ${formatTime(updates.end)}`;

    const result = await TtmlMetadataService.mutateEdits(
      currentSessionId,
      checksum,
      [{
        op: 'update',
        index: edit.index,
        data: {
          title: updates.title,
          suggestion: updates.suggestion,
          improvement: updates.improvement,
          priority: updates.priority,
          startTime: updates.start,
          endTime: updates.end,
        },
      }],
      revisionNotes,
      authToken,
    );

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleDeleteEdit = useCallback(async (edit: TTMLElement) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (edit.index === undefined) {
      throw new Error('Edit index is missing');
    }

    const revisionNotes = `# Revision Notes\n\n## Summary\nManually deleted edit suggestion "${edit.title}" at ${formatTime(edit.start)}.\n\n## Changes Made\n- Deleted edit\n\n## Timestamp\n- Position: ${formatTime(edit.start)}`;

    const result = await TtmlMetadataService.mutateEdits(
      currentSessionId,
      checksum,
      [{ op: 'remove', index: edit.index }],
      revisionNotes,
      authToken,
    );

    setSelectedClipEdit(null);
    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleClipEditModalSave = useCallback(async (element: TimelineLooseElement, updates: ClipEditUpdates) => {
    if (selectedClipEdit?.type === 'clip') {
      await handleUpdateClip(element as TTMLElement, updates);
      return;
    }
    await handleUpdateEdit(element as TTMLElement, updates);
  }, [handleUpdateClip, handleUpdateEdit, selectedClipEdit?.type]);

  const handleClipEditModalDelete = useCallback(async (element: TimelineLooseElement) => {
    if (selectedClipEdit?.type === 'clip') {
      await handleDeleteClip(element as TTMLElement);
      return;
    }
    await handleDeleteEdit(element as TTMLElement);
  }, [handleDeleteClip, handleDeleteEdit, selectedClipEdit?.type]);

  const handleUpdateClipTiming = useCallback(async (clip: TTMLElement, newStart: number, newEnd: number) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (clip.index === undefined) {
      throw new Error('Clip index is missing');
    }

    const originalStart = (clip as any)._originalStart ?? clip.start;
    const revisionNotes = `# Revision Notes\n\n## Summary\nMoved clip "${clip.title}" via timeline drag.\n\n## Changes Made\n- Timing: ${formatTime(originalStart)} -> ${formatTime(newStart)} to ${formatTime(newEnd)}\n\n## Timestamp\n- New position: ${formatTime(newStart)} - ${formatTime(newEnd)}`;

    const result = await TtmlMetadataService.mutateClips(
      currentSessionId,
      checksum,
      [{
        op: 'update',
        index: clip.index,
        data: {
          startTime: newStart,
          endTime: newEnd,
        },
      }],
      revisionNotes,
      authToken,
    );

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  const handleUpdateEditTiming = useCallback(async (edit: TTMLElement, newStart: number, newEnd: number) => {
    const { checksum, authToken, currentSessionId } = requireStructuredMutationContext();
    if (edit.index === undefined) {
      throw new Error('Edit index is missing');
    }

    const originalStart = (edit as any)._originalStart ?? edit.start;
    const revisionNotes = `# Revision Notes\n\n## Summary\nMoved edit "${edit.title}" via timeline drag.\n\n## Changes Made\n- Timing: ${formatTime(originalStart)} -> ${formatTime(newStart)} to ${formatTime(newEnd)}\n\n## Timestamp\n- New position: ${formatTime(newStart)} - ${formatTime(newEnd)}`;

    const result = await TtmlMetadataService.mutateEdits(
      currentSessionId,
      checksum,
      [{
        op: 'update',
        index: edit.index,
        data: {
          startTime: newStart,
          endTime: newEnd,
        },
      }],
      revisionNotes,
      authToken,
    );

    publishStructuredRevision(result, revisionNotes);
  }, [publishStructuredRevision, requireStructuredMutationContext]);

  // Element (clip/edit) drag handlers
  const handleElementDragStart = useCallback((
    e: React.MouseEvent,
    element: TTMLElement,
    type: 'clip' | 'edit',
    handle: 'left' | 'right' | 'move'
  ) => {
    e.stopPropagation();
    e.preventDefault();

    setElementDragState({
      element,
      type,
      handle,
      initialStart: element.start,
      initialEnd: element.end,
      initialMouseX: e.clientX
    });
    setElementDragPreview({ start: element.start, end: element.end });
  }, []);

  // Global mouse move handler for element dragging
  useEffect(() => {
    if (!elementDragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      const progressBar = document.querySelector('[data-progress-bar]') as HTMLElement;
      if (!progressBar) return;

      const rect = progressBar.getBoundingClientRect();
      const deltaX = e.clientX - elementDragState.initialMouseX;
      const deltaPercent = deltaX / rect.width;
      const displayDuration = viewportDuration || timelineDisplayDuration;
      const deltaTime = deltaPercent * displayDuration;

      let newStart = elementDragState.initialStart;
      let newEnd = elementDragState.initialEnd;
      const minDuration = 1; // Minimum 1 second duration

      switch (elementDragState.handle) {
        case 'left':
          // Resize from left edge
          newStart = Math.max(0, Math.min(elementDragState.initialStart + deltaTime, newEnd - minDuration));
          break;
        case 'right':
          // Resize from right edge
          newEnd = Math.min(displayDuration, Math.max(elementDragState.initialEnd + deltaTime, newStart + minDuration));
          break;
        case 'move':
          // Move entire element
          const duration = elementDragState.initialEnd - elementDragState.initialStart;
          newStart = Math.max(0, Math.min(elementDragState.initialStart + deltaTime, displayDuration - duration));
          newEnd = newStart + duration;
          break;
      }

      setElementDragPreview({ start: newStart, end: newEnd });

      // Set drag highlight time for transcript highlighting (use midpoint)
      setDragHighlightTime((newStart + newEnd) / 2);

      // Set tooltip position
      setElementDragTooltipPosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = async () => {
      if (elementDragState && elementDragPreview) {
        const { element, type, handle, initialStart, initialEnd } = elementDragState;
        const { start: newStart, end: newEnd } = elementDragPreview;

        // Check if position actually changed (drag) vs just a click
        const didDrag = Math.abs(newStart - initialStart) > 0.5 || Math.abs(newEnd - initialEnd) > 0.5;

        if (didDrag) {
          // It was a drag - update timing
          try {
            if (type === 'clip') {
              await handleUpdateClipTiming(element, newStart, newEnd);
            } else {
              await handleUpdateEditTiming(element, newStart, newEnd);
            }
          } catch (error) {
            console.error('[CentralAudioPlayer] Failed to update element timing:', error);
          }
        } else if (handle === 'move') {
          // It was a click on the move handle - open the modal
          setSelectedClipEdit({ element, type });
          setShowClipEditModal(true);
        }
        // Left/right handles: click does nothing, just cancels
      }

      setElementDragState(null);
      setElementDragPreview(null);
      setElementDragTooltipPosition(null);
      setDragHighlightTime(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [elementDragState, elementDragPreview, handleUpdateClipTiming, handleUpdateEditTiming, setDragHighlightTime, timelineDisplayDuration, viewportDuration]);

  // Handle showing floating action popup after hover delay
  const handleElementHoverStart = useCallback((
    element: TTMLElement,
    type: 'clip' | 'edit',
    e: React.MouseEvent
  ) => {
    // Clear any existing timeout
    if (floatingActionTimeoutRef.current) {
      clearTimeout(floatingActionTimeoutRef.current);
    }

    // Set timeout to show floating action after 600ms
    floatingActionTimeoutRef.current = setTimeout(() => {
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      setFloatingActionElement({
        element,
        type,
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    }, 600);
  }, []);

  const handleElementHoverEnd = useCallback(() => {
    // Clear the show timeout
    if (floatingActionTimeoutRef.current) {
      clearTimeout(floatingActionTimeoutRef.current);
      floatingActionTimeoutRef.current = null;
    }
    // Set a short timeout to hide the popup - this gets cancelled if mouse enters the popup
    floatingActionTimeoutRef.current = setTimeout(() => {
      setFloatingActionElement(null);
    }, 150);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (floatingActionTimeoutRef.current) {
        clearTimeout(floatingActionTimeoutRef.current);
      }
    };
  }, []);

  // Load comments initially - skip API call if we have CDN-bundled comments
  useEffect(() => {
    if (!sessionId || isLoadingComments) return;
    if (!commentsAllowed) {
      setComments([]);
      setIsLoadingComments(false);
      return;
    }

    // If we have pre-loaded comments from CDN bundle, use them directly (no API call)
    if (fromCdn && effectiveIsPublicView && initialComments) {
      console.log('[CentralAudioPlayer] Using pre-loaded comments from CDN bundle:', initialComments.length, 'comments');
      setComments(initialComments);
      setIsLoadingComments(false);
      return;
    }

    const loadCommentsFromApi = async () => {
      setIsLoadingComments(true);
      try {
        let response;

        if (effectiveIsPublicView && effectiveShareToken) {
          // For public views, use the share API (only if not from CDN)
          response = await fetch(`${API_BASE_URL}/api/public/share/${effectiveShareToken}/comments`);
        } else if (token) {
          // For authenticated users
          response = await fetch(`${API_BASE_URL}/api/sessions/${sessionId}/comments`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
        } else {
          // No auth available
          return;
        }

        if (response && response.ok) {
          const data = await response.json();
          console.log('[CentralAudioPlayer] Loaded comments from API:', data.comments?.length || 0, 'comments');
          setComments(data.comments || []);
        }
      } catch (error) {
        console.error('Failed to load comments from API:', error);
      } finally {
        setIsLoadingComments(false);
      }
    };

    loadCommentsFromApi();
  }, [sessionId, token, effectiveIsPublicView, effectiveShareToken, fromCdn, initialComments, commentsAllowed]);

  if (isCollapsed) {
    return (
      <div className="flex-shrink-0">
        <button
          onClick={() => setIsCollapsed(false)}
          className={`w-full px-4 py-2 flex items-center justify-center gap-2 transition-colors ${
            isDarkMode 
              ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
              : 'bg-white hover:bg-gray-50 text-gray-700 border-t'
          }`}
        >
          <ChevronUp size={16} />
          <span className="text-sm">Show Audio Player</span>
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Main Player Bar */}
      <div className={`flex-shrink-0 relative ${
        isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      } border-t`} style={{ overflow: 'visible' }}>
        <div className="relative" style={{ overflow: 'visible' }}>
          {/* Progress/Waveform Bar */}
          <div
            ref={progressBarRef}
            data-progress-bar
            data-testid="timeline-viewport"
            className={`relative cursor-pointer overflow-hidden ${timelineViewportHeightClass}`}
            onClick={handleTimelineClick}
            onMouseMove={handleTimelineHover}
            onMouseLeave={clearHoveredTimelineState}
            onPointerDown={handleTimelinePointerDown}
            onPointerMove={handleTimelinePointerMove}
            onPointerUp={(e) => finishTimelinePointerPan(e.pointerId, e.currentTarget)}
            onPointerCancel={(e) => finishTimelinePointerPan(e.pointerId, e.currentTarget)}
            onWheel={handleTimelineWheel}
            onTouchStart={handleTimelineTouchStart}
            onTouchMove={handleTimelineTouchMove}
            onTouchEnd={handleTimelineTouchEnd}
            onTouchCancel={handleTimelineTouchEnd}
            style={{ touchAction: 'none' }}
          >
            {/* Background */}
            <div className={`absolute inset-0 ${
              isDarkMode ? 'bg-gray-800' : 'bg-[#273142]'
            }`} />

            {/* Removed waveform visualization - showing speaker segments more clearly */}

            {/* Progress bar - solid orange bar at bottom - moved after all layers for visibility */}
            <div
              className="absolute inset-y-0 left-0"
              style={{
                width: timelineContentWidth || '100%',
                transform: `translateX(${-timelineContentOffsetX}px)`,
                pointerEvents: 'none',
              }}
            >

            {/* Highlight hovered time range */}
            {hoveredTimeRange && totalDuration > 0 && (
              <>
                {/* Bright blue highlight bar */}
                <div
                  className="absolute top-0 bottom-0 pointer-events-none z-50"
                  style={{
                    left: `${(hoveredTimeRange.start / totalDuration) * 100}%`,
                    width: hoveredTimeRange.end 
                      ? `${((hoveredTimeRange.end - hoveredTimeRange.start) / totalDuration) * 100}%`
                      : '3px',
                    background: 'rgba(59, 130, 246, 0.5)',
                    borderLeft: '2px solid rgb(59, 130, 246)',
                    borderRight: hoveredTimeRange.end ? '2px solid rgb(59, 130, 246)' : 'none'
                  }}
                />
                {/* Additional bright blue bar above the timeline */}
                <div
                  className="absolute -top-2 pointer-events-none z-50 animate-pulse"
                  style={{
                    left: `${(hoveredTimeRange.start / totalDuration) * 100}%`,
                    width: hoveredTimeRange.end 
                      ? `${((hoveredTimeRange.end - hoveredTimeRange.start) / totalDuration) * 100}%`
                      : '3px',
                    height: '4px',
                    background: 'rgb(59, 130, 246)',
                    boxShadow: '0 0 10px rgba(59, 130, 246, 0.8), 0 2px 4px rgba(59, 130, 246, 0.6)'
                  }}
                />
                {/* Extra glow effect */}
                <div
                  className="absolute top-0 bottom-0 pointer-events-none z-49"
                  style={{
                    left: `${(hoveredTimeRange.start / totalDuration) * 100}%`,
                    width: hoveredTimeRange.end 
                      ? `${((hoveredTimeRange.end - hoveredTimeRange.start) / totalDuration) * 100}%`
                      : '2px',
                    background: 'rgba(147, 197, 253, 0.2)',
                    filter: 'blur(8px)'
                  }}
                />
              </>
            )}
            
            {/* Layer stack - speakers, topics, clips - z-index 10 so retention overlay can appear above */}
            <div key={timelineVisualizationKey} className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
              {/* Speaker segments - bottom layer - edit-aware with time remapping */}
              {editAwareSpeakerSegments.map((segment, index) => {
                const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                const startPercent = (segment.start / displayDuration) * 100;
                const widthPercent = ((segment.end - segment.start) / displayDuration) * 100;

                return (
                  <div
                    key={`speaker-${index}`}
                    className="absolute"
                    style={{
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`,
                      top: timelineLayout.speakerTop,
                      height: timelineLayout.speakerHeight,
                      background: getSpeakerGradientValue(segment.speaker, isDarkMode),
                      opacity: 0.85
                    }}
                  />
                );
              })}
              
              {/* Topics / Questions - middle layer - edit-aware with time remapping and sorting */}
              <React.Fragment key={timelineVisualizationKey}>
              {overlayMode === 'questions' ? (
                // Question spans overlay
                editAwareQuestionSpans.map((span) => {
                  const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                  const startPercent = (span.start / displayDuration) * 100;
                  const widthPercent = ((span.end - span.start) / displayDuration) * 100;
                  const isHovered = hoveredQuestionSpan?.questionNumber === span.questionNumber;

                  // Alternate between two question span colors
                  const isEven = span.questionNumber % 2 === 0;
                  const bgColor = isEven
                    ? 'rgba(147, 130, 255, 0.75)' // purple-ish for even
                    : 'rgba(100, 160, 255, 0.75)'; // blue-ish for odd

                  return (
                    <div
                      key={span.id}
                      className="absolute"
                      style={{
                        left: `${startPercent}%`,
                        width: `${widthPercent}%`,
                        top: timelineLayout.topicTop,
                        height: timelineLayout.topicHeight,
                      }}
                    >
                      {/* Question span background */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(to right, ${bgColor}, ${bgColor.replace('0.75', '0.95')}, ${bgColor})`,
                          opacity: 0.95
                        }}
                      />

                      {/* White scanlines overlay when hovered */}
                      {isHovered && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: `repeating-linear-gradient(
                              90deg,
                              rgba(255, 255, 255, 0.3) 0px,
                              rgba(255, 255, 255, 0.3) 2px,
                              transparent 2px,
                              transparent 4px
                            )`,
                            zIndex: 10
                          }}
                        />
                      )}

                      {/* Question number badge */}
                      {!isCompactTouchLayout && (
                        <div
                          className="absolute left-0 top-0 bg-black text-white text-xs px-1 rounded-br z-20"
                          style={{ fontSize: '10px', lineHeight: '12px' }}
                        >
                          Q{span.questionNumber}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // Topic spans overlay (existing behavior)
                editAwareTopics.map((topic, index) => {
                  // Use pre-computed remapped times from editAwareTopics
                  const displayStart = topic.start;
                  const displayEnd = topic.end;
                  const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                  const startPercent = (displayStart / displayDuration) * 100;
                  const widthPercent = ((displayEnd - displayStart) / displayDuration) * 100;
                  const topicColor = getTopicColor(topic);

                  const isHovered = hoveredTopic && hoveredTopic.id === topic.id;

                  return (
                    <div
                      key={topic.id}
                      className="absolute"
                      style={{
                        left: `${startPercent}%`,
                        width: `${widthPercent}%`,
                        top: timelineLayout.topicTop,
                        height: timelineLayout.topicHeight,
                      }}
                    >
                      {/* Topic bar background */}
                      <div
                        className="absolute inset-0"
                        style={{
                          background: getTopicGradient(topic),
                          opacity: 0.95
                        }}
                      />

                      {/* White scanlines overlay when hovered */}
                      {isHovered && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            background: `repeating-linear-gradient(
                              90deg,
                              rgba(255, 255, 255, 0.3) 0px,
                              rgba(255, 255, 255, 0.3) 2px,
                              transparent 2px,
                              transparent 4px
                            )`,
                            zIndex: 10
                          }}
                        />
                      )}

                      {/* Topic number - hidden on mobile - uses original segment number */}
                      {!isCompactTouchLayout && (
                        <div
                          className="absolute left-0 top-0 bg-black text-white text-xs px-1 rounded-br z-20"
                          style={{ fontSize: '10px', lineHeight: '12px' }}
                        >
                          {topic.segmentNumber || (index + 1)}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              </React.Fragment>
              
              {/* Edits - upper middle layer with red scanlines - edit-aware with time remapping */}
              {ttmlElements.edits
                .filter(edit => !isEditedMode || !isElementInCutRegion(edit.start, edit.end))
                .map((edit, index) => {
                // Remap times for edited mode
                const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                // Use drag preview position if this edit is being dragged
                const isDraggingThis = elementDragState?.element.id === edit.id && elementDragState?.type === 'edit';
                const displayStart = isDraggingThis && elementDragPreview
                  ? elementDragPreview.start
                  : (remapToEditedTime(edit.start) ?? edit.start);
                const displayEnd = isDraggingThis && elementDragPreview
                  ? elementDragPreview.end
                  : (remapToEditedTime(edit.end) ?? edit.end);
                const startPercent = (displayStart / displayDuration) * 100;
                const widthPercent = ((displayEnd - displayStart) / displayDuration) * 100;
                const isHovered = hoveredEdit?.id === edit.id;
                const canEditEdit = canEditTimelineMetadata;

                return (
                  <div
                    key={edit.id}
                    className={`absolute overflow-hidden transition-all duration-150 ${
                      isHovered || isDraggingThis ? 'opacity-100' : 'opacity-80'
                    } ${isDraggingThis ? 'z-50' : ''}`}
                    style={{
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`,
                      top: timelineLayout.editTop,
                      height: timelineLayout.editHeight,
                      background: `repeating-linear-gradient(
                        0deg,
                        rgb(239, 68, 68) 0px,
                        rgb(239, 68, 68) 2px,
                        transparent 2px,
                        transparent 4px
                      )`,
                      boxShadow: isHovered || isDraggingThis ? '0 0 12px 4px rgba(239, 68, 68, 0.6)' : 'none',
                      pointerEvents: 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (canEditEdit) {
                        handleElementHoverStart(edit, 'edit', e);
                      }
                    }}
                    onMouseLeave={() => {
                      handleElementHoverEnd();
                    }}
                  />
                );
              })}
              
              {/* Clips - same layer as edits with blue scanlines - edit-aware with time remapping */}
              {ttmlElements.clips
                .filter(clip => !isEditedMode || !isElementInCutRegion(clip.start, clip.end))
                .map((clip, index) => {
                // Remap times for edited mode
                const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                // Use drag preview position if this clip is being dragged
                const isDraggingThis = elementDragState?.element.id === clip.id && elementDragState?.type === 'clip';
                const displayStart = isDraggingThis && elementDragPreview
                  ? elementDragPreview.start
                  : (remapToEditedTime(clip.start) ?? clip.start);
                const displayEnd = isDraggingThis && elementDragPreview
                  ? elementDragPreview.end
                  : (remapToEditedTime(clip.end) ?? clip.end);
                const startPercent = (displayStart / displayDuration) * 100;
                const widthPercent = ((displayEnd - displayStart) / displayDuration) * 100;
                const isHovered = hoveredClip?.id === clip.id;
                const canEditClip = canEditTimelineMetadata;

                return (
                  <div
                    key={clip.id}
                    className={`absolute overflow-hidden transition-all duration-150 ${
                      isHovered || isDraggingThis ? 'opacity-100' : 'opacity-90'
                    } ${isDraggingThis ? 'z-50' : ''}`}
                    style={{
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`,
                      top: timelineLayout.clipTop,
                      height: timelineLayout.clipHeight,
                      background: `repeating-linear-gradient(
                        0deg,
                        rgb(59, 130, 246) 0px,
                        rgb(59, 130, 246) 2px,
                        transparent 2px,
                        transparent 4px
                      )`,
                      boxShadow: isHovered || isDraggingThis ? '0 0 12px 4px rgba(59, 130, 246, 0.6)' : 'none',
                      pointerEvents: 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (canEditClip) {
                        handleElementHoverStart(clip, 'clip', e);
                      }
                    }}
                    onMouseLeave={() => {
                      handleElementHoverEnd();
                    }}
                  />
                );
              })}

              {/* References - invisible hover areas for tooltip (visual orbs shown in TimelineReferences component) */}
              {/* Hover detection for references is handled by handleProgressBarHover based on time position */}

              {/* Edit Cut Markers - Red overlays showing cut regions */}
              {hasEditCuts && editCuts.length > 0 && totalDuration > 0 && (
                <>
                  {editCuts.map((cut, index) => {
                    const leftPercent = (cut.startTime / totalDuration) * 100;
                    const widthPercent = ((cut.endTime - cut.startTime) / totalDuration) * 100;
                    return (
                      <div
                        key={cut.id || index}
                        className={`absolute top-0 bottom-0 pointer-events-none z-20 ${
                          isEditedMode
                            ? 'bg-red-500/0' // Hidden in edited mode
                            : 'bg-red-500/30 border-x border-red-500/50' // Show in original mode
                        }`}
                        style={{
                          left: `${leftPercent}%`,
                          width: `${widthPercent}%`
                        }}
                        title={cut.reason || `Cut ${index + 1}`}
                      >
                        {/* Diagonal stripes pattern for cut regions */}
                        {!isEditedMode && (
                          <div
                            className="absolute inset-0 opacity-30"
                            style={{
                              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(239, 68, 68, 0.5) 4px, rgba(239, 68, 68, 0.5) 8px)'
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {/* Search match markers - thin red vertical lines */}
              {searchMatches && searchMatches.length > 0 && totalDuration > 0 && (
                <>
                  {searchMatches.map((match, index) => {
                    const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                    // Get the start time of the match
                    const matchTime = match.timestampSeconds;

                    // In edit mode, skip matches in cut regions and remap time
                    if (isEditedMode && timelineMapper) {
                      if (timelineMapper.isInCutRegion(matchTime, matchTime)) {
                        return null;
                      }
                      const editedTime = timelineMapper.originalToEdited(matchTime);
                      if (editedTime === null) return null;
                      const leftPercent = (editedTime / displayDuration) * 100;
                      return (
                        <div
                          key={`search-${index}`}
                          className="absolute top-0 bottom-0"
                          style={{
                            left: `${leftPercent}%`,
                            width: '2px',
                            background: 'rgb(239, 68, 68)',
                            boxShadow: '0 0 4px rgba(239, 68, 68, 0.6)',
                            zIndex: 25
                          }}
                          title={`Search match ${index + 1}`}
                        />
                      );
                    }

                    // Original mode - use time directly
                    const leftPercent = (matchTime / displayDuration) * 100;
                    return (
                      <div
                        key={`search-${index}`}
                        className="absolute top-0 bottom-0"
                        style={{
                          left: `${leftPercent}%`,
                          width: '2px',
                          background: 'rgb(239, 68, 68)',
                          boxShadow: '0 0 4px rgba(239, 68, 68, 0.6)',
                          zIndex: 25
                        }}
                        title={`Search match ${index + 1}`}
                      />
                    );
                  })}
                </>
              )}

              {/* Gap cluster markers — horizontal bars with individual gap ticks */}
              {gapClusters && gapClusters.length > 0 && totalDuration > 0 && (
                <>
                  {gapClusters.map((cluster, index) => {
                    const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                    const startPercent = (cluster.startTime / displayDuration) * 100;
                    const widthPercent = ((cluster.endTime - cluster.startTime) / displayDuration) * 100;
                    const barColor = cluster.severity === 'high' ? 'rgba(239, 68, 68, 0.85)'
                      : cluster.severity === 'medium' ? 'rgba(245, 158, 11, 0.80)'
                      : 'rgba(234, 179, 8, 0.75)';
                    return (
                      <React.Fragment key={`gap_${index}`}>
                        {/* Solid horizontal bar for the cluster span */}
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${startPercent}%`,
                            width: `${Math.max(widthPercent, 0.3)}%`,
                            bottom: timelineLayout.gapBarBottom,
                            height: timelineLayout.gapBarHeight,
                            backgroundColor: barColor,
                            zIndex: 31,
                            borderRadius: '1px',
                          }}
                          title={`Gap cluster: ${cluster.gapCount} gaps, z=${cluster.avgZScore} (${cluster.editorialSignal})`}
                        />
                        {/* Individual gap ticks — thin vertical lines above the bar */}
                        {cluster.gaps && cluster.gaps.map((gap, gi) => {
                          const gapPercent = (gap.startTime / displayDuration) * 100;
                          return (
                            <div
                              key={`gap_${index}_t${gi}`}
                              className="absolute pointer-events-none"
                              style={{
                                left: `${gapPercent}%`,
                                width: '1px',
                                bottom: timelineLayout.gapTickBottom,
                                height: `${Math.min(4 + gap.zScore * 3, 20)}px`,
                                backgroundColor: barColor,
                                zIndex: 31,
                              }}
                            />
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </>
              )}

              {/* Non-clustered gap ticks — standalone gaps above threshold but not in any cluster */}
              {nonClusteredGaps && nonClusteredGaps.length > 0 && totalDuration > 0 && (
                <>
                  {nonClusteredGaps.map((gap, gi) => {
                    const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
                    const gapPercent = (gap.startTime / displayDuration) * 100;
                    const tickColor = gap.zScore >= 2.5 ? 'rgba(239, 68, 68, 0.7)'
                      : gap.zScore >= 1.5 ? 'rgba(245, 158, 11, 0.65)'
                      : 'rgba(234, 179, 8, 0.6)';
                    return (
                      <div
                        key={`ncgap_${gi}`}
                        className="absolute pointer-events-none"
                        style={{
                          left: `${gapPercent}%`,
                          width: '1px',
                          bottom: timelineLayout.nonClusteredGapBottom,
                          height: `${Math.min(4 + gap.zScore * 3, 20)}px`,
                          backgroundColor: tickColor,
                          zIndex: 31,
                        }}
                        title={`Gap: ${gap.gapDuration.toFixed(1)}s, z=${gap.zScore}`}
                      />
                    );
                  })}
                </>
              )}

            </div>

            {/* Retention line chart overlay - wrapped in positioned container */}
            {retentionData && retentionData.retentionData && retentionData.retentionData.length > 0 && totalDuration > 0 && retentionMode !== 'off' && retentionMode !== 'drops' && (() => {
              // Use edit-aware duration for positioning
              const displayDuration = isEditedMode ? effectiveDuration : totalDuration;

              // Get retention value based on mode
              const getRetentionValue = (point: any) => {
                const rel = point.relativeRetention;
                const abs = point.absoluteRetention / 100; // Convert % to 0-1
                switch (retentionMode) {
                  case 'absolute':
                    // For absolute, use log scale to better show variations in lower ranges
                    // Log scale: log(1 + abs*9) / log(10) gives nice 0-1 mapping
                    return Math.log10(1 + abs * 9);
                  case 'adjusted':
                    return rel * Math.sqrt(abs);
                  case 'relative':
                  default:
                    return rel;
                }
              };

              // Detect if retention data is in edit space or original space
              // Heuristic: compare videoDurationSeconds to effectiveDuration vs totalDuration
              const retentionVideoDuration = retentionData.videoDurationSeconds || 0;
              const isRetentionInEditSpace = isEditedMode && (
                // If video duration is closer to edited duration, assume edit space
                Math.abs(retentionVideoDuration - effectiveDuration) < Math.abs(retentionVideoDuration - totalDuration) ||
                // Or if video duration is significantly less than original, assume edit space
                retentionVideoDuration < totalDuration * 0.9
              );

              // Helper to get X position percentage for a timestamp
              const getDisplayX = (timestamp: number): number | null => {
                if (isEditedMode) {
                  if (isRetentionInEditSpace) {
                    // Retention data is already in edit space - use directly
                    return (timestamp / displayDuration) * 100;
                  } else {
                    // Retention data is in original space - need to remap
                    if (timelineMapper && timelineMapper.isInCutRegion(timestamp, timestamp)) {
                      return null; // Skip points in cut regions
                    }
                    const editedTime = timelineMapper?.originalToEdited(timestamp);
                    if (editedTime === null || editedTime === undefined) return null;
                    return (editedTime / displayDuration) * 100;
                  }
                }
                return (timestamp / totalDuration) * 100;
              };

              // Calculate min/max retention values to scale Y-axis dynamically
              const retentionValues = retentionData.retentionData.map(getRetentionValue);
              const minRetention = Math.min(...retentionValues);
              const maxRetention = Math.max(...retentionValues);
              const range = maxRetention - minRetention;
              // Add 10% padding above and below, ensure minimum range for visual clarity
              const padding = Math.max(range * 0.1, 0.02);
              const scaleMin = Math.max(0, minRetention - padding);
              const scaleMax = maxRetention + padding;
              const scaleRange = scaleMax - scaleMin;

              // Function to convert retention value to Y coordinate (0-100, where 0 is top)
              const retentionToY = (retention: number) => {
                const normalized = (retention - scaleMin) / scaleRange;
                return 100 - (normalized * 100); // Flip so higher values are at top
              };

              // Calculate where the average/reference line should be based on mode
              let avgLineY: number;
              let showAvgLine = true;
              switch (retentionMode) {
                case 'absolute':
                  // For absolute, show line at 50% retention (log scaled)
                  avgLineY = retentionToY(Math.log10(1 + 0.5 * 9));
                  break;
                case 'adjusted': {
                  // For adjusted, use YouTube's 0.5 baseline × sqrt(mean absolute)
                  const avgAbs = retentionData.retentionData.reduce((sum: number, p: any) => sum + p.absoluteRetention / 100, 0) / retentionData.retentionData.length;
                  avgLineY = retentionToY(0.5 * Math.sqrt(avgAbs));
                  break;
                }
                case 'relative':
                default:
                  // For relative mode, use 0.5 as the baseline per YouTube API documentation
                  // relativeRetentionPerformance: 0.5 = average compared to similar videos
                  // Values > 0.5 = above average, < 0.5 = below average
                  avgLineY = retentionToY(0.5);
                  // Only show if 0.5 is within the visible range
                  showAvgLine = avgLineY >= 0 && avgLineY <= 100;
                  break;
              }

              // YouTube baseline is 0.5 for relative retention
              const youtubeBaseline = 0.5;

              // Color for the graph based on mode
              const graphColor = retentionMode === 'absolute' ? '#232E3F' :
                                 retentionMode === 'adjusted' ? '#8a6115' :
                                 '#FFB52B';

              // Traffic light color function for relative/adjusted modes
              // Returns green for above avg, yellow for near avg, red for below avg
              // Uses YouTube's 0.5 baseline - values > 0.5 are above average
              const getTrafficLightColor = (retValue: number): string => {
                if (retentionMode !== 'relative' && retentionMode !== 'adjusted') {
                  return graphColor;
                }
                // Use YouTube's 0.5 baseline for relative retention
                const baseline = retentionMode === 'relative'
                  ? youtubeBaseline
                  : youtubeBaseline * Math.sqrt(
                      retentionData.retentionData.reduce((sum: number, p: any) => sum + p.absoluteRetention / 100, 0) / retentionData.retentionData.length
                    );
                const deviation = retValue - baseline;

                // Color based on deviation from 0.5 baseline
                // Scale: 0.5 = average, so +0.3 = 80%, +0.15 = 65%, etc.
                if (deviation >= 0.25) return '#22c55e'; // green - significantly above (75%+)
                if (deviation >= 0.15) return '#84cc16'; // lime - well above (65%+)
                if (deviation >= 0.05) return '#eab308'; // yellow - above average (55%+)
                if (deviation >= -0.05) return '#fbbf24'; // amber - near average (45-55%)
                if (deviation >= -0.15) return '#f97316'; // orange - below average (35-45%)
                return '#ef4444'; // red - significantly below (<35%)
              };

              // Prepare line segments with colors for traffic light mode
              const prepareLineSegments = () => {
                const videoDuration = retentionData.videoDurationSeconds || totalDuration;
                const segments: { x1: number; y1: number; x2: number; y2: number; color: string }[] = [];

                let prevPoint: { x: number; y: number; value: number } | null = null;

                for (const point of retentionData.retentionData) {
                  const timestamp = point.timestampSeconds !== undefined
                    ? point.timestampSeconds
                    : point.ratio * videoDuration;
                  const x = getDisplayX(timestamp);
                  if (x === null) {
                    prevPoint = null;
                    continue;
                  }
                  const clampedX = Math.max(0, Math.min(100, x));
                  const retValue = getRetentionValue(point);
                  const y = retentionToY(retValue);

                  if (prevPoint !== null) {
                    // Color based on midpoint value between segments
                    const midValue = (prevPoint.value + retValue) / 2;
                    segments.push({
                      x1: prevPoint.x,
                      y1: prevPoint.y,
                      x2: clampedX,
                      y2: y,
                      color: getTrafficLightColor(midValue)
                    });
                  }

                  prevPoint = { x: clampedX, y, value: retValue };
                }

                return segments;
              };

              const useTrafficLightColors = retentionMode === 'relative' || retentionMode === 'adjusted';

              return (
              <div className="absolute inset-0 z-20 pointer-events-none">
                {/* Dark semi-transparent background for retention chart visibility */}
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                />
                <svg
                  className="absolute inset-0"
                  width="100%"
                  height="100%"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  {/* Reference line - white dashed */}
                  {showAvgLine && avgLineY >= 0 && avgLineY <= 100 && (
                    <line
                      x1="0"
                      y1={avgLineY}
                      x2="100"
                      y2={avgLineY}
                      stroke="rgba(255,255,255,0.8)"
                      strokeWidth="1.5"
                      strokeDasharray="6,4"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}

                  {/* Filled area - shows deviation from reference */}
                  <path
                    d={(() => {
                      const videoDuration = retentionData.videoDurationSeconds || totalDuration;
                      const points = retentionData.retentionData.map((point: any) => {
                        const timestamp = point.timestampSeconds !== undefined
                          ? point.timestampSeconds
                          : point.ratio * videoDuration;
                        const x = getDisplayX(timestamp);
                        if (x === null) return null; // Skip points in cut regions
                        const clampedX = Math.max(0, Math.min(100, x));
                        const y = retentionToY(getRetentionValue(point));
                        return `${clampedX},${y}`;
                      }).filter((p): p is string => p !== null && parseFloat(p.split(',')[0]) <= 100);
                      if (points.length === 0) return '';
                      const firstY = points[0].split(',')[1];
                      const fillToY = showAvgLine && avgLineY >= 0 && avgLineY <= 100 ? avgLineY : 50;
                      return `M0,${firstY} L${points.join(' L')} L100,${points[points.length-1].split(',')[1]} L100,${fillToY} L0,${fillToY} Z`;
                    })()}
                    fill={graphColor.replace(')', ', 0.4)').replace('rgb', 'rgba').replace('#', '')}
                    style={{ fill: `${graphColor}66` }} // 40% opacity
                  />

                  {/* White glow/outline for the line */}
                  <polyline
                    points={(() => {
                      const videoDuration = retentionData.videoDurationSeconds || totalDuration;
                      return retentionData.retentionData.map((point: any) => {
                        const timestamp = point.timestampSeconds !== undefined
                          ? point.timestampSeconds
                          : point.ratio * videoDuration;
                        const x = getDisplayX(timestamp);
                        if (x === null) return null; // Skip points in cut regions
                        const clampedX = Math.max(0, Math.min(100, x));
                        const y = retentionToY(getRetentionValue(point));
                        return `${clampedX},${y}`;
                      }).filter((p): p is string => p !== null && parseFloat(p.split(',')[0]) <= 100).join(' ');
                    })()}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.6)"
                    strokeWidth="5"
                    vectorEffect="non-scaling-stroke"
                  />

                  {/* Main retention line - traffic light colors for relative/adjusted, solid for others */}
                  {useTrafficLightColors ? (
                    // Draw colored segments for relative/adjusted modes
                    <>
                      {prepareLineSegments().map((seg, idx) => (
                        <line
                          key={idx}
                          x1={seg.x1}
                          y1={seg.y1}
                          x2={seg.x2}
                          y2={seg.y2}
                          stroke={seg.color}
                          strokeWidth="3"
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                    </>
                  ) : (
                    <polyline
                      points={(() => {
                        const videoDuration = retentionData.videoDurationSeconds || totalDuration;
                        return retentionData.retentionData.map((point: any) => {
                          const timestamp = point.timestampSeconds !== undefined
                            ? point.timestampSeconds
                            : point.ratio * videoDuration;
                          const x = getDisplayX(timestamp);
                          if (x === null) return null; // Skip points in cut regions
                          const clampedX = Math.max(0, Math.min(100, x));
                          const y = retentionToY(getRetentionValue(point));
                          return `${clampedX},${y}`;
                        }).filter((p): p is string => p !== null && parseFloat(p.split(',')[0]) <= 100).join(' ');
                      })()}
                      fill="none"
                      stroke={graphColor}
                      strokeWidth="3"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}

                  {/* Hover dot on retention line */}
                  {hoveredTime !== null && (() => {
                    const retention = getRetentionAtTime(hoveredTime);
                    if (!retention) return null;
                    // Use edit-aware X position
                    const x = getDisplayX(hoveredTime);
                    if (x === null) return null; // Don't show dot if in cut region
                    // Transform the value same way as graph for Y position
                    let graphValue = retention.value;
                    if (retentionMode === 'absolute') {
                      // Apply same log transform as graph
                      graphValue = Math.log10(1 + retention.value * 9);
                    }
                    const y = retentionToY(graphValue);
                    return (
                      <>
                        {/* Outer glow circle */}
                        <circle
                          cx={x}
                          cy={y}
                          r="8"
                          fill={retention.color}
                          opacity="0.3"
                        />
                        {/* White ring */}
                        <circle
                          cx={x}
                          cy={y}
                          r="5"
                          fill="white"
                          stroke={retention.color}
                          strokeWidth="2"
                          vectorEffect="non-scaling-stroke"
                        />
                        {/* Inner dot */}
                        <circle
                          cx={x}
                          cy={y}
                          r="3"
                          fill={retention.color}
                        />
                      </>
                    );
                  })()}
                </svg>
              </div>
              );
            })()}

            {/* Drops bar chart - viewer started/stopped watching */}
            {retentionData && retentionData.retentionData && retentionData.retentionData.length > 1 && totalDuration > 0 && retentionMode === 'drops' && (() => {
              const videoDuration = retentionData.videoDurationSeconds || totalDuration;
              // Use edit-aware duration for positioning
              const displayDuration = isEditedMode ? effectiveDuration : totalDuration;

              // Detect if retention data is in edit space or original space
              const retentionVideoDuration = retentionData.videoDurationSeconds || 0;
              const isRetentionInEditSpace = isEditedMode && (
                Math.abs(retentionVideoDuration - effectiveDuration) < Math.abs(retentionVideoDuration - totalDuration) ||
                retentionVideoDuration < totalDuration * 0.9
              );

              // Helper to get display X position (0-100) for a time
              const getDropsDisplayX = (time: number): number | null => {
                if (isEditedMode) {
                  if (isRetentionInEditSpace) {
                    // Retention data is already in edit space - use directly
                    return (time / displayDuration) * 100;
                  } else {
                    // Retention data is in original space - need to remap
                    if (timelineMapper && timelineMapper.isInCutRegion(time, time)) {
                      return null;
                    }
                    const editedTime = timelineMapper?.originalToEdited(time);
                    if (editedTime === null || editedTime === undefined) return null;
                    return (editedTime / displayDuration) * 100;
                  }
                }
                return (time / totalDuration) * 100;
              };
              const totalViews = retentionData.videoStats?.views || 100000; // Default for calculation

              // Calculate drops and gains between consecutive retention points
              // Group into 30-second buckets for cleaner visualization
              const bucketSize = 30; // seconds
              const numBuckets = Math.ceil(videoDuration / bucketSize);
              const drops: { start: number; end: number; dropped: number; gained: number }[] = [];

              for (let i = 0; i < numBuckets; i++) {
                const bucketStart = i * bucketSize;
                const bucketEnd = Math.min((i + 1) * bucketSize, videoDuration);

                // Find retention values at bucket boundaries
                const getAbsRetentionAt = (time: number) => {
                  const point = retentionData.retentionData.reduce((closest: any, p: any) => {
                    const pTime = p.timestampSeconds !== undefined ? p.timestampSeconds : p.ratio * videoDuration;
                    const closestTime = closest.timestampSeconds !== undefined ? closest.timestampSeconds : closest.ratio * videoDuration;
                    return Math.abs(pTime - time) < Math.abs(closestTime - time) ? p : closest;
                  });
                  return point.absoluteRetention / 100;
                };

                const retentionStart = getAbsRetentionAt(bucketStart);
                const retentionEnd = getAbsRetentionAt(bucketEnd);

                // Calculate viewers at each point
                const viewersStart = totalViews * retentionStart;
                const viewersEnd = totalViews * retentionEnd;

                // Net change - negative means dropped, positive means gained
                const netChange = viewersEnd - viewersStart;

                // For most of the video, viewers drop (negative netChange = dropped)
                // Sometimes viewers jump to a timestamp (positive netChange = gained)
                drops.push({
                  start: bucketStart,
                  end: bucketEnd,
                  dropped: netChange < 0 ? Math.abs(netChange) : 0,
                  gained: netChange > 0 ? netChange : 0
                });
              }

              // Use 75th percentile for scaling instead of max to magnify smaller changes
              // This clips the large initial drop but makes smaller drops visible
              const sortedDrops = [...drops.map(d => d.dropped)].sort((a, b) => a - b);
              const sortedGains = [...drops.map(d => d.gained)].sort((a, b) => a - b);
              const percentile75Index = Math.floor(sortedDrops.length * 0.75);
              const percentile75Drop = sortedDrops[percentile75Index] || 1;
              const percentile75Gain = sortedGains[percentile75Index] || 1;
              // Use 5x magnification by setting scale to 75th percentile (values above will clip)
              const scaleValue = Math.max(percentile75Drop, percentile75Gain, 1);

              // Filter drops to exclude cut regions and calculate display positions
              const displayDrops = drops.map(bucket => {
                const midTime = (bucket.start + bucket.end) / 2;
                const xStart = getDropsDisplayX(bucket.start);
                const xEnd = getDropsDisplayX(bucket.end);
                // If either boundary is in a cut region, skip this bucket
                if (xStart === null || xEnd === null) return null;
                return { ...bucket, xStart, xEnd, width: xEnd - xStart };
              }).filter((d): d is NonNullable<typeof d> => d !== null);

              return (
                <div className="absolute inset-0 z-20 pointer-events-none">
                  {/* Dark semi-transparent background */}
                  <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} />

                  {/* Center line */}
                  <div className="absolute left-0 right-0 top-1/2 h-px bg-white opacity-50" />

                  {/* Bar chart - using percentage-based positioning for edit mode support */}
                  <svg
                    className="absolute inset-0"
                    width="100%"
                    height="100%"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    {displayDrops.map((bucket, i) => {
                      const barWidth = Math.max(bucket.width * 0.8, 0.5); // 80% of bucket width, min 0.5%
                      const xCenter = bucket.xStart + bucket.width / 2;
                      const x = xCenter - barWidth / 2;

                      // Dropped viewers - red bars going down from center (capped at 45)
                      const dropHeight = Math.min((bucket.dropped / scaleValue) * 45, 45);
                      // Gained viewers - green bars going up from center (capped at 45)
                      const gainHeight = Math.min((bucket.gained / scaleValue) * 45, 45);

                      return (
                        <g key={i}>
                          {/* Dropped viewers - bars below center (red) */}
                          {dropHeight > 0 && (
                            <rect
                              x={x}
                              y={50}
                              width={barWidth}
                              height={dropHeight}
                              fill="#ef4444"
                              opacity="0.9"
                            />
                          )}
                          {/* Gained viewers - bars above center (green) */}
                          {gainHeight > 0 && (
                            <rect
                              x={x}
                              y={50 - gainHeight}
                              width={barWidth}
                              height={gainHeight}
                              fill="#22c55e"
                              opacity="0.9"
                            />
                          )}
                        </g>
                      );
                    })}
                  </svg>

                  {/* Legend */}
                  <div className="absolute bottom-1 left-2 flex items-center gap-3 text-xs text-white">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-500 rounded-sm" />
                      <span>Stopped</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-[#FFB52B] rounded-sm" />
                      <span>Started</span>
                    </div>
                  </div>

                  {/* Hover info */}
                  {hoveredTime !== null && (() => {
                    const bucketIndex = Math.floor(hoveredTime / bucketSize);
                    if (bucketIndex >= 0 && bucketIndex < drops.length) {
                      const bucket = drops[bucketIndex];
                      return (
                        <div
                          className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded"
                        >
                          {bucket.dropped > 0 && (
                            <span className="text-red-400">-{Math.round(bucket.dropped).toLocaleString()} left</span>
                          )}
                          {bucket.dropped > 0 && bucket.gained > 0 && <span className="mx-1">|</span>}
                          {bucket.gained > 0 && (
                            <span className="text-[#ffcf74]">+{Math.round(bucket.gained).toLocaleString()} joined</span>
                          )}
                          {bucket.dropped === 0 && bucket.gained === 0 && (
                            <span className="text-gray-400">No change</span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              );
            })()}

            {/* Progress bar - solid orange bar at bottom - on top of all layers */}
            <ProgressBar totalDuration={totalDuration} displayDuration={isEditedMode ? effectiveDuration : undefined} />

            {/* Vertical orange playhead bar - lower z-index than cursor */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-orange-500 pointer-events-none z-10"
              style={{
                left: (isEditedMode ? effectiveDuration : totalDuration) > 0
                  ? `${(currentTime / (isEditedMode ? effectiveDuration : totalDuration)) * 100}%`
                  : '0%'
              }}
            />

            {/* Progress indicator - playhead cursor - higher z-index */}
            <PlayheadCursor
              totalDuration={totalDuration}
              displayDuration={isEditedMode ? effectiveDuration : undefined}
            />

            {/* Hover time indicator - wider and brighter */}
            {hoveredTime !== null && (() => {
              // Convert original time to display position
              const displayTime = isEditedMode && timelineMapper
                ? (timelineMapper.originalToEdited(hoveredTime) ?? hoveredTime)
                : hoveredTime;
              const displayDuration = isEditedMode ? effectiveDuration : totalDuration;
              return (
                <div
                  className="absolute top-0 w-1 h-full bg-white pointer-events-none opacity-70"
                  style={{
                    left: `${(displayTime / displayDuration) * 100}%`
                  }}
                />
              );
            })()}

            </div>
          </div>

          {/* Hover time tooltip - moved outside overflow-hidden container */}
          {hoveredTime !== null && (() => {
            // Calculate display time/duration for positioning in edit mode
            const tooltipDisplayTime = isEditedMode && timelineMapper
              ? (timelineMapper.originalToEdited(hoveredTime) ?? hoveredTime)
              : hoveredTime;
            const tooltipPositionRatio = progressBarWidth > 0 && hoveredDisplayX !== null
              ? hoveredDisplayX / progressBarWidth
              : 0;

            const retention = getRetentionAtTime(hoveredTime);
            const drops = getDropsAtTime(hoveredTime);
            const hasRetention = retention && retentionData?.retentionData?.length;
            const hasDrops = drops && (drops.dropped > 0 || drops.gained > 0);
            const hasTtmlElements = hoveredTopic || hoveredClip || hoveredEdit || hoveredReference || hoveredQuestionSpan;
            const isMobileTimelineTooltip = isCompactTouchLayout;
            const tooltipSurfaceClass = isMobileTimelineTooltip
              ? isDarkMode
                ? 'border-white/10 bg-[#1a2332]/[0.98] text-white'
                : 'border-[#d6dce4] bg-white/[0.98] text-[#232E3F]'
              : isDarkMode
                ? 'border-gray-600 bg-gray-700 text-white'
                : 'border-[#d6dce4] bg-white text-[#232E3F]';
            const tooltipPrimaryTextClass = isDarkMode ? 'text-white' : 'text-[#232E3F]';
            const tooltipSecondaryTextClass = isDarkMode ? 'text-gray-300' : 'text-[#4a586e]';
            const tooltipMutedTextClass = isDarkMode ? 'text-gray-400' : 'text-[#6b7280]';
            const tooltipSlateAccentClass = isDarkMode ? 'text-[#d9e2f1]' : 'text-[#36506b]';
            const tooltipSlateBadgeClass = isDarkMode
              ? 'bg-[#314159]/50 text-[#d9e2f1]'
              : 'bg-[#e7eef7] text-[#36506b]';
            const desktopTooltipWidth = 320;
            const tooltipViewportPadding = 16;
            const progressBarRect = progressBarRef.current?.getBoundingClientRect() || null;
            const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
            const desktopTooltipResolvedWidth = viewportWidth > 0
              ? Math.max(220, Math.min(desktopTooltipWidth, viewportWidth - (tooltipViewportPadding * 2)))
              : desktopTooltipWidth;
            const hoveredViewportX = progressBarRect
              ? progressBarRect.left + (hoveredDisplayX ?? 0)
              : (hoveredDisplayX ?? 0);
            const desktopTooltipViewportLeft = viewportWidth > 0
              ? Math.min(
                  Math.max(hoveredViewportX - (desktopTooltipResolvedWidth / 2), tooltipViewportPadding),
                  viewportWidth - desktopTooltipResolvedWidth - tooltipViewportPadding
                )
              : hoveredViewportX - (desktopTooltipResolvedWidth / 2);
            const desktopTooltipLeft = progressBarRect
              ? desktopTooltipViewportLeft - progressBarRect.left
              : Math.max(0, hoveredDisplayX ?? 0);
            const getTooltipBadgeClass = (tone: 'neutral' | 'success' | 'gold' | 'danger') => {
              switch (tone) {
                case 'success':
                  return isDarkMode
                    ? 'bg-emerald-950/35 text-emerald-300'
                    : 'bg-[#EAF7EF] text-[#2D7250]';
                case 'gold':
                  return isDarkMode
                    ? 'border border-brand-yellow/25 bg-brand-yellow/15 text-brand-yellow'
                    : 'border border-[#F2D9A6] bg-[#FFF4D9] text-[#8A6100]';
                case 'danger':
                  return isDarkMode
                    ? 'bg-red-950/40 text-red-300'
                    : 'bg-[#FDE8E8] text-[#B54747]';
                case 'neutral':
                default:
                  return tooltipSlateBadgeClass;
              }
            };

            const sanitizeTooltipMetadata = (content: string, preserveLineBreaks = false): string => (
              toPlainMetadataText(
                String(content || '')
                  .replace(/<br\s*\/?>/gi, '\n')
                  .replace(/&gt;/g, '>')
                  .replace(/&lt;/g, '<')
                  .replace(/&amp;/g, '&')
                  .replace(/&quot;/g, '"')
                  .replace(/&apos;/g, "'"),
                preserveLineBreaks
              )
            );

            // Helper function to parse topic justification (filters out Optimal Placement)
            const getTopicJustification = (content: string): string | null => {
              const sanitizedContent = sanitizeTooltipMetadata(content, true);
              const justMatch = sanitizedContent.match(/Justification:\s*([^\n]+)/);
              if (!justMatch) return null;
              let result = sanitizeTooltipMetadata(justMatch[1]);
              // Filter out "Optimal Placement:" section if it somehow got included
              result = result.replace(/Optimal Placement:.*$/i, '').trim();
              return result || null;
            };

            // Helper function to parse clip content for tooltip display
            // Handles both new format: [CLIP] Title | Type | Description | Visual
            // And old format: [CLIP] Title<br/>Type: description<br/>Visual: suggestion
            const parseClipForTooltip = (content: string): { title?: string; type?: string; description?: string } => {
              let decoded = sanitizeTooltipMetadata(
                content.replace(/\[CLIP\]\s*/gi, ''),
                true
              );

              // Check for new pipe-delimited format: Title | Type | Description | Visual
              if (decoded.includes('|')) {
                const parts = decoded.split('|').map(p => p.trim());
                return {
                  title: sanitizeTooltipMetadata(parts[0] || '') || undefined,
                  type: sanitizeTooltipMetadata(parts[1] || '') || undefined,
                  description: sanitizeTooltipMetadata(parts[2] || '') || undefined
                  // Visual (parts[3]) is intentionally omitted for tooltip display
                };
              }

              // Old format: parse line by line
              const lines = decoded.split('\n').map(l => l.trim()).filter(l => l.length > 0);

              let title: string | undefined;
              let type: string | undefined;
              let description: string | undefined;

              for (const line of lines) {
                // Skip Visual suggestions - these are for editors only
                if (line.toLowerCase().startsWith('visual:')) continue;

                // Check for clip type indicators
                const lowerLine = line.toLowerCase();
                if (lowerLine.includes('viral hook') || lowerLine.includes('viral:')) {
                  type = 'Viral Hook';
                  // Extract description if after colon
                  const colonIdx = line.indexOf(':');
                  if (colonIdx > -1) {
                    const desc = line.substring(colonIdx + 1).trim();
                    if (desc && !description) description = desc;
                  }
                } else if (lowerLine.includes('educational nugget') || lowerLine.includes('educational:')) {
                  type = 'Educational Nugget';
                  const colonIdx = line.indexOf(':');
                  if (colonIdx > -1) {
                    const desc = line.substring(colonIdx + 1).trim();
                    if (desc && !description) description = desc;
                  }
                } else if (lowerLine.includes('humorous aside') || lowerLine.includes('humorous:') || lowerLine.includes('humor:')) {
                  type = 'Humorous Aside';
                  const colonIdx = line.indexOf(':');
                  if (colonIdx > -1) {
                    const desc = line.substring(colonIdx + 1).trim();
                    if (desc && !description) description = desc;
                  }
                } else if (!title) {
                  // First non-type, non-visual line is the title
                  title = line;
                } else if (!description) {
                  // Subsequent lines are description
                  description = line;
                }
              }

              return { title, type, description };
            };

            // Helper function to parse edit content for tooltip display
            // Handles new pipe-delimited format: [EDIT] Title | Priority | Suggestion | Improvement
            // And old format with Suggestion:, Priority:, etc labels
            const parseEditForTooltip = (content: string): { title?: string; suggestion?: string; improvement?: string; priority?: string } => {
              let decoded = sanitizeTooltipMetadata(
                content.replace(/\[EDIT\]\s*/gi, ''),
                true
              );

              // Check for new pipe-delimited format: Title | Priority | Suggestion | Improvement
              if (decoded.includes('|')) {
                const parts = decoded.split('|').map(p => p.trim());
                return {
                  title: sanitizeTooltipMetadata(parts[0] || '') || undefined,
                  priority: sanitizeTooltipMetadata(parts[1] || '') || undefined,
                  suggestion: sanitizeTooltipMetadata(parts[2] || '') || undefined,
                  improvement: sanitizeTooltipMetadata(parts[3] || '') || undefined
                };
              }

              // Old format: parse with Suggestion:, Priority:, etc labels
              decoded = decoded.replace(/(?=Suggestion:|Improvement:|Priority:|Note:)/g, '\n');

              const lines = decoded.split('\n').map(l => l.trim()).filter(l => l.length > 0);

              let title: string | undefined;
              let suggestion: string | undefined;
              let improvement: string | undefined;
              let priority: string | undefined;

              for (const line of lines) {
                if (line.startsWith('Suggestion:')) {
                  const text = line.replace('Suggestion:', '').trim();
                  if (!suggestion) suggestion = text;
                  else if (!improvement) improvement = text;
                } else if (line.startsWith('Improvement:')) {
                  improvement = line.replace('Improvement:', '').trim();
                } else if (line.startsWith('Priority:')) {
                  priority = line.replace('Priority:', '').trim();
                } else if (!title && !suggestion) {
                  title = line;
                }
              }

              return { title, suggestion, improvement, priority };
            };

            // Helper function to clean element content (remove tags)
            const cleanContent = (content: string, tag: string): string => {
              return content
                .replace(new RegExp(`\\[${tag}\\]\\s*`, 'gi'), '')
                .replace(/<br\s*\/?>/gi, ' ')
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0)
                .slice(0, 3) // Take first 3 lines max
                .join(' | ');
            };

            return (
              <div
                className={isMobileTimelineTooltip
                  ? `absolute left-0 right-0 rounded-t-2xl border px-4 py-3 text-xs shadow-2xl ${tooltipSurfaceClass}`
                  : `absolute rounded border px-3 py-2 text-xs pointer-events-none shadow-lg ${tooltipSurfaceClass}`}
                style={isMobileTimelineTooltip
                  ? {
                      bottom: 'calc(100% + 8px)',
                      zIndex: 1000,
                    }
                  : {
                      bottom: 'calc(100% + 8px)',
                      left: `${desktopTooltipLeft}px`,
                      width: `${desktopTooltipResolvedWidth}px`,
                      maxWidth: `calc(100vw - ${tooltipViewportPadding * 2}px)`,
                      transform: 'translateX(0)',
                      zIndex: 1000,
                    }}
              >
                {isMobileTimelineTooltip ? (
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${
                        isDarkMode ? 'text-[#ffcf74]' : 'text-[#8a6115]'
                      }`}>
                        Timeline Details
                      </div>
                      <div className={`mt-1 font-medium ${
                        isDarkMode ? 'text-gray-200' : 'text-[#232E3F]'
                      }`}>
                        {formatTime(tooltipDisplayTime)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearHoveredTimelineState}
                      className={`shrink-0 rounded-full border p-1.5 transition-colors ${
                        isDarkMode
                          ? 'border-white/10 bg-white/[0.04] text-gray-300 hover:bg-white/[0.1]'
                          : 'border-[#d6dce4] bg-[#f5f7fa] text-[#5b687c] hover:bg-[#e9edf2]'
                      }`}
                      aria-label="Close timeline details"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className={`mb-1 whitespace-nowrap font-medium ${tooltipSecondaryTextClass}`}>
                    {formatTime(tooltipDisplayTime)}
                  </div>
                )}

                {hoveredSpeaker && (
                  <div className="mb-2 flex items-center gap-2">
                    <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${tooltipMutedTextClass}`}>
                      Speaker
                    </span>
                    <div className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 text-[11px] ${tooltipSlateBadgeClass}`}>
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: hoveredSpeaker.color }}
                      />
                      <span className={tooltipPrimaryTextClass}>{hoveredSpeaker.label}</span>
                    </div>
                  </div>
                )}

                {/* Topic / Question Section */}
                {overlayMode === 'questions' && hoveredQuestionSpan ? (
                  <div className="border-l-2 border-[#a78bfa] pl-2 mb-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-semibold text-[#a78bfa]">Q{hoveredQuestionSpan.questionNumber}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        hoveredQuestionSpan.question.questionType === 'implicit'
                          ? getTooltipBadgeClass('gold')
                          : getTooltipBadgeClass('neutral')
                      }`}>
                        {hoveredQuestionSpan.question.questionType === 'implicit' ? 'Implicit' : 'Explicit'}
                      </span>
                      {hoveredQuestionSpan.question.askedBy && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${getTooltipBadgeClass('neutral')}`}>
                          {hoveredQuestionSpan.question.askedBy}
                        </span>
                      )}
                    </div>
                    <div className={`mt-0.5 break-words text-[11px] font-medium ${isDarkMode ? 'text-white' : 'text-[#232E3F]'} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                      {hoveredQuestionSpan.question.questionText.length > 140
                        ? hoveredQuestionSpan.question.questionText.substring(0, 140) + '...'
                        : hoveredQuestionSpan.question.questionText}
                    </div>
                    {hoveredQuestionSpan.question.answerSummary && (
                      <div className={`mt-0.5 break-words text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-[#4a586e]'} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                        {hoveredQuestionSpan.question.answerSummary.length > 120
                          ? hoveredQuestionSpan.question.answerSummary.substring(0, 120) + '...'
                          : hoveredQuestionSpan.question.answerSummary}
                      </div>
                    )}
                    {hoveredQuestionSpan.question.topic && (
                      <div className={`mt-0.5 text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Topic: {hoveredQuestionSpan.question.topic}
                      </div>
                    )}
                  </div>
                ) : hoveredTopic && (
                  <div className="border-l-2 border-[#ffcf74] pl-2 mb-2">
                    {(() => {
                      const score = getTopicScoreParts(hoveredTopic);
                      return (
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-[#ffcf74]">Topic {ttmlElements.topics.indexOf(hoveredTopic) + 1}</span>
                          {hoveredTopic.title && <span className={tooltipPrimaryTextClass}>: {toPlainMetadataText(hoveredTopic.title)}</span>}
                          <span className="text-xs opacity-60 ml-1">({getTopicVisualizationLabel()}: {score.score ?? '?'}/{score.maxScore ?? '?'})</span>
                        </div>
                      );
                    })()}
                    {getTopicJustification(hoveredTopic.content) && (
                      <div className={`mt-0.5 break-words text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-[#4a586e]'} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                        {getTopicJustification(hoveredTopic.content)}
                      </div>
                    )}
                  </div>
                )}

                {/* Clip Section */}
                {hoveredClip && (() => {
                  const parsed = parseClipForTooltip(hoveredClip.content);
                  return (
                    <div className="border-l-2 border-[#d9e2f1] pl-2 mb-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`font-semibold ${tooltipSlateAccentClass}`}>Clip</span>
                        {parsed.type && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            parsed.type.toLowerCase().includes('viral')
                              ? getTooltipBadgeClass('danger')
                              : parsed.type.toLowerCase().includes('educational')
                                ? getTooltipBadgeClass('success')
                                : getTooltipBadgeClass('gold')
                          }`}>
                            {parsed.type}
                          </span>
                        )}
                      </div>
                      {parsed.title && (
                        <div className={`mt-0.5 text-[11px] font-medium ${isDarkMode ? 'text-white' : 'text-[#232E3F]'}`}>
                          {parsed.title}
                        </div>
                      )}
                      {parsed.description && (
                        <div className={`mt-0.5 break-words text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-[#4a586e]'} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                          {parsed.description.length > 120 ? parsed.description.substring(0, 120) + '...' : parsed.description}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Edit Section */}
                {hoveredEdit && (() => {
                  const parsed = parseEditForTooltip(hoveredEdit.content);
                  return (
                    <div className="border-l-2 border-red-400 pl-2 mb-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-semibold text-red-400">Edit</span>
                        {parsed.priority && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            parsed.priority.toLowerCase().includes('high')
                              ? getTooltipBadgeClass('danger')
                              : parsed.priority.toLowerCase().includes('medium')
                                ? getTooltipBadgeClass('gold')
                                : getTooltipBadgeClass('neutral')
                          }`}>
                            {parsed.priority}
                          </span>
                        )}
                      </div>
                      {/* Title already shown in Topic section above, so skip here */}
                      {parsed.suggestion && (
                        <div className={`mt-0.5 break-words text-[11px] ${isDarkMode ? 'text-gray-300' : 'text-[#4a586e]'} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                          <span className={tooltipMutedTextClass}>Suggestion:</span> {parsed.suggestion.length > 100 ? parsed.suggestion.substring(0, 100) + '...' : parsed.suggestion}
                        </div>
                      )}
                      {parsed.improvement && (
                        <div className={`mt-0.5 break-words text-[10px] italic ${tooltipMutedTextClass} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                          <span className="text-[#ffcf74]">Improvement:</span> {parsed.improvement.length > 80 ? parsed.improvement.substring(0, 80) + '...' : parsed.improvement}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Reference Section */}
                {hoveredReference && (
                  <div className="border-l-2 border-[#c9a85a] pl-2 mb-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-semibold text-[#c9a85a]">Reference</span>
                      {hoveredReference.url && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${tooltipSlateBadgeClass}`}>
                          Has Link
                        </span>
                      )}
                    </div>
                    {hoveredReference.title && (
                      <div className={`mt-0.5 text-[11px] font-medium ${isDarkMode ? 'text-white' : 'text-[#232E3F]'}`}>
                        {toPlainMetadataText(hoveredReference.title)}
                      </div>
                    )}
                    {hoveredReference.category && (
                      <div className={`mt-0.5 text-[10px] ${tooltipSecondaryTextClass}`}>
                        {toPlainMetadataText(hoveredReference.category)}
                      </div>
                    )}
                    {hoveredReference.context && (
                      <div className={`mt-0.5 break-words text-[10px] ${tooltipMutedTextClass} ${isMobileTimelineTooltip ? 'max-w-none' : 'max-w-xs'}`}>
                        {(() => {
                          const context = toPlainMetadataText(hoveredReference.context, true);
                          return context.length > 80 ? `${context.substring(0, 80)}...` : context;
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Retention info */}
                {hasRetention && (
                  <div className={`mt-1 flex items-center gap-1.5 ${isMobileTimelineTooltip ? 'flex-wrap' : 'whitespace-nowrap'}`}>
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: retention.color }}
                    />
                    <span>Retention:</span>
                    <span className="font-semibold" style={{ color: retention.color }}>
                      {retention.label}
                    </span>
                    <span className="opacity-70">
                      ({retention.displayValue})
                    </span>
                  </div>
                )}

                {/* Drops info */}
                {hasDrops && (
                  <div className={`mt-1 flex items-center gap-1.5 ${isMobileTimelineTooltip ? 'flex-wrap' : 'whitespace-nowrap'}`}>
                    {drops.dropped > 0 && (
                      <>
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                        <span className="text-red-400">-{Math.round(drops.dropped).toLocaleString()} left</span>
                      </>
                    )}
                    {drops.dropped > 0 && drops.gained > 0 && <span className="mx-1 opacity-50">|</span>}
                    {drops.gained > 0 && (
                      <>
                        <span className="inline-block w-2 h-2 rounded-full bg-[#FFB52B]" />
                        <span className="text-[#ffcf74]">+{Math.round(drops.gained).toLocaleString()} joined</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Timeline Comments */}
          {showTimelineComments && commentsAllowed && sessionId && totalDuration > 0 && progressBarWidth > 0 && (
            <div
              ref={timelineSecondaryViewportRef}
              data-testid="timeline-secondary-viewport"
              className="relative z-40"
              onPointerDown={handleTimelinePointerDown}
              onPointerMove={handleTimelinePointerMove}
              onPointerUp={(e) => finishTimelinePointerPan(e.pointerId, e.currentTarget)}
              onPointerCancel={(e) => finishTimelinePointerPan(e.pointerId, e.currentTarget)}
              onWheel={handleTimelineWheel}
              onTouchStart={handleTimelineTouchStart}
              onTouchMove={handleTimelineTouchMove}
              onTouchEnd={handleTimelineTouchEnd}
              onTouchCancel={handleTimelineTouchEnd}
              style={{ overflow: 'visible', touchAction: 'none' }}
            >
              <div className="relative">
                {/* Comments timeline */}
                <TimelineCommentsWrapper
                  comments={comments}
                  duration={totalDuration}
                  onAddComment={handleAddComment}
                  onSelectComment={handleSelectComment}
                  selectedCommentId={selectedCommentId}
                  isDarkMode={isDarkMode}
                  width={progressBarWidth}
                  height={timelineHeight}
                  viewportStart={viewportStart}
                  viewportEnd={viewportEnd}
                  timeToX={timeToX}
                  onHeightChange={setTimelineHeight}
                />

                {/* Reference orbs timeline - edit-aware with time remapping */}
                {/* Show if there are references OR if user can edit (to show add button) */}
                {(ttmlElements.references.length > 0 || canEditTimelineMetadata) && (
                  <TimelineReferencesWrapper
                    references={ttmlElements.references
                      .filter(ref => !isEditedMode || !isElementInCutRegion(ref.start, ref.end))
                      .map(ref => {
                        if (!isEditedMode || !timelineMapper || !hasEditCuts) return ref;
                        let displayStart = timelineMapper.originalToEdited(ref.start);
                        const displayEnd = timelineMapper.originalToEdited(ref.end);

                        // If start is in a cut region but end is not, use the cut's end time
                        if (displayStart === null && displayEnd !== null) {
                          const cut = timelineMapper.getCutAt(ref.start);
                          if (cut) {
                            displayStart = timelineMapper.originalToEdited(cut.endTime);
                          }
                        }

                        return {
                          ...ref,
                          _originalStart: ref.start,
                          _originalEnd: ref.end,
                          start: displayStart ?? ref.start,
                          end: displayEnd ?? ref.end
                        };
                      })}
                    duration={isEditedMode ? effectiveDuration : totalDuration}
                    width={progressBarWidth}
                    isDarkMode={isDarkMode}
                    viewportStart={viewportStart}
                    viewportEnd={viewportEnd}
                    timeToX={timeToX}
                    xToTime={xToTime}
                    onSelectReference={(ref) => {
                      setSelectedReference(ref as TTMLElement);
                      setShowReferencesModal(true);
                    }}
                    selectedReferenceId={selectedReference?.id}
                    onAddReference={handleOpenAddReferenceModal}
                    canEdit={canEditTimelineMetadata}
                    onDragStart={handleReferenceDragStart}
                    onDragMove={handleReferenceDragMove}
                    onDragEnd={handleReferenceDragEnd}
                  />
                )}
              </div>
            </div>
          )}

          {/* Controls Bar */}
          <div className="px-4 py-2 relative">
            <div className="flex items-center justify-between gap-2 md:gap-4 relative">
              {/* Left side - Play button, Session info and time */}
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                {/* Play/Pause button */}
                <button
                  onClick={() => isPlaying ? pause() : play()}
                  className={`p-2 rounded-full transition-all flex-shrink-0 ${
                    isDarkMode
                      ? 'bg-brand-yellow text-brand-navy hover:bg-[#f6c15e]'
                      : 'bg-brand-yellow text-brand-navy hover:bg-[#f0a91a]'
                  }`}
                  title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
                >
                  {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                </button>
                
                <TimeDisplay
                  totalDuration={totalDuration}
                  isDarkMode={isDarkMode}
                  displayDuration={isEditedMode ? effectiveDuration : undefined}
                  timeMapper={isEditedMode && timelineMapper ? (t) => timelineMapper.originalToEdited(t) : undefined}
                  showKeyboardHints={!isCompactTouchLayout}
                />
              </div>

              {/* Center - Timeline controls */}
              <div className="flex flex-1 justify-center">
                <div className="relative">
                  <button
                    ref={timelineMenuRef}
                    type="button"
                    onClick={() => setShowTimelineMenu((prev) => !prev)}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs transition-colors ${
                      isDarkMode
                        ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    title="Timeline controls"
                  >
                    <Settings size={14} />
                    <span className="hidden sm:inline">Timeline</span>
                  </button>

                  <AnchoredPopover
                    isOpen={showTimelineMenu}
                    onClose={() => setShowTimelineMenu(false)}
                    anchorRef={timelineMenuRef}
                    align="center"
                    offset={12}
                    className={`w-[min(24rem,calc(100vw-1rem))] rounded-2xl border p-4 shadow-2xl ${
                      isDarkMode ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-900'
                    }`}
                  >
                    <div className="space-y-4">
                      {!isCompactTouchLayout && Object.keys(speakerLabels || {}).length > 0 && (
                        <div className="space-y-2">
                          <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Speakers
                          </div>
                          <div className="max-h-32 space-y-2 overflow-y-auto pr-1">
                            {Object.entries(speakerLabels || {}).map(([speakerId, label]) => {
                              const percentage = speakerPercentages[parseInt(speakerId, 10)] || 0;
                              return (
                                <div
                                  key={speakerId}
                                  className={`flex items-center justify-between rounded-xl border px-3 py-2 text-xs ${
                                    isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                  <div
                                        className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: getSpeakerColorValue(parseInt(speakerId, 10), isDarkMode) }}
                                      />
                                    <span className="truncate">{label}</span>
                                  </div>
                                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{percentage}%</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {selectedTtmlFile && (
                        <div className="space-y-2">
                          <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Timeline Layers
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {ttmlElements.clips.length > 0 && (
                              <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                                isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                              }`}>
                                <div className="w-3 h-3 rounded-sm" style={{ background: 'repeating-linear-gradient(0deg, rgb(59, 130, 246) 0px, rgb(59, 130, 246) 1px, transparent 1px, transparent 2px)' }} />
                                <span>Clips</span>
                              </div>
                            )}
                            {ttmlElements.edits.length > 0 && (
                              <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                                isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                              }`}>
                                <div className="w-3 h-3 rounded-sm" style={{ background: 'repeating-linear-gradient(0deg, rgb(239, 68, 68) 0px, rgb(239, 68, 68) 1px, transparent 1px, transparent 2px)' }} />
                                <span>Edits</span>
                              </div>
                            )}
                            {ttmlElements.references.length > 0 && (
                              <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs ${
                                isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                              }`}>
                                <div className="w-2.5 h-2.5 rotate-45" style={{ backgroundColor: '#FFB52B' }} />
                                <span>Refs</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Overlay mode toggle: Topics vs Questions */}
                      {(ttmlElements.topics.length > 0 || questionData.length > 0) && (
                        <div className="space-y-2">
                          <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Overlay Mode
                          </div>
                          <div className="flex gap-2">
                            {ttmlElements.topics.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setOverlayMode('topics')}
                                className={`rounded-xl px-3 py-2 text-xs font-medium transition-colors ${
                                  questionData.length > 0 ? 'flex-1' : 'w-full'
                                } ${
                                  overlayMode === 'topics'
                                    ? 'bg-[#FFB52B] text-[#232E3F]'
                                    : isDarkMode
                                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                Topics ({ttmlElements.topics.length})
                              </button>
                            )}
                            {questionData.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setOverlayMode('questions')}
                                className={`rounded-xl px-3 py-2 text-xs font-medium transition-colors ${
                                  ttmlElements.topics.length > 0 ? 'flex-1' : 'w-full'
                                } ${
                                  overlayMode === 'questions'
                                    ? 'bg-[#a78bfa] text-white'
                                    : isDarkMode
                                      ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                Questions ({questionData.length})
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {ttmlElements.topics.length > 0 && overlayMode === 'topics' && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setShowGradient(!showGradient);
                            }}
                            className={`rounded-xl px-3 py-2 text-xs transition-colors ${
                              isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                            title={showGradient ? 'Switch to traffic light colors' : 'Switch to gradient colors'}
                          >
                            Palette: {showGradient ? 'Gradient' : 'Traffic'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const modes: TopicVisualizationMode[] = ['overall', 'substance', 'resonance', 'clarity', 'production', 'creative_risk'];
                              const currentIndex = modes.indexOf(topicVisualizationMode);
                              const nextIndex = (currentIndex + 1) % modes.length;
                              setTopicVisualizationMode(modes[nextIndex]);
                            }}
                            className={`rounded-xl px-3 py-2 text-xs transition-colors ${
                              isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            Rank: {getTopicVisualizationLabel()}
                          </button>
                          {retentionData?.retentionData?.length ? (
                            <button
                              type="button"
                              onClick={cycleRetentionMode}
                              className={`col-span-2 rounded-xl px-3 py-2 text-xs text-white transition-opacity hover:opacity-90 ${getRetentionModeInfo().color}`}
                              title={getRetentionModeInfo().title}
                            >
                              Retention: {getRetentionModeInfo().label}
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </AnchoredPopover>
                </div>
              </div>

              {/* Right side - Volume and actions */}
              <div className="flex items-center gap-2 md:gap-3">
                <div className="flex items-center gap-1">
                  {!isCompactTouchLayout && (
                    <button
                      type="button"
                      aria-label="Zoom out"
                      data-testid="timeline-zoom-out"
                      onClick={handleZoomOut}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                      title="Zoom out"
                    >
                      <ZoomOut size={16} />
                    </button>
                  )}
                  {!isCompactTouchLayout && (
                    <button
                      type="button"
                      aria-label="Zoom in"
                      data-testid="timeline-zoom-in"
                      onClick={handleZoomIn}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700 text-gray-300'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                      title="Zoom in"
                    >
                      <ZoomIn size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Reset zoom"
                    data-testid="timeline-reset-zoom"
                    onClick={handleResetZoom}
                    className={`p-2 rounded-lg transition-colors ${
                      isTimelineZoomed
                        ? isDarkMode
                          ? 'bg-brand-yellow text-brand-navy hover:bg-[#f6c15e]'
                          : 'bg-[#ffe2a6] text-brand-navy hover:bg-[#ffd47c]'
                        : isDarkMode
                          ? 'hover:bg-gray-700 text-gray-400'
                          : 'hover:bg-gray-100 text-gray-500'
                    }`}
                    title="Reset zoom"
                  >
                    <RefreshCcw size={16} />
                  </button>
                  {!cursorSyncEnabled && (
                    <button
                      type="button"
                      aria-label="Resync transcript and timeline"
                      onClick={() => requestCursorSync()}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'bg-brand-yellow text-brand-navy hover:bg-[#f6c15e]'
                          : 'bg-[#ffe2a6] text-brand-navy hover:bg-[#ffd47c]'
                      }`}
                      title="Resync transcript and timeline to the playhead"
                    >
                      <LocateFixed size={16} />
                    </button>
                  )}
                </div>

                {/* Only show comment controls if comments are allowed */}
                {commentsAllowed && (
                  <>
                    {/* Timeline comments toggle button */}
                    <button
                      onClick={() => setShowTimelineComments(!showTimelineComments)}
                      className={`p-2 rounded-lg transition-colors ${
                        showTimelineComments
                          ? isDarkMode
                            ? 'bg-brand-yellow text-brand-navy'
                            : 'bg-[#ffe2a6] text-brand-navy'
                          : isDarkMode
                            ? 'hover:bg-gray-700 text-gray-400'
                            : 'hover:bg-gray-100 text-gray-600'
                      }`}
                      title={showTimelineComments ? 'Hide timeline comments' : 'Show timeline comments'}
                    >
                      {showTimelineComments ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    
                    {/* Comments panel toggle button with count */}
                    <button
                      onClick={() => {
                        if (!showCommentsPanel) {
                          // Opening panel - clear any pending timestamp
                          setPendingCommentTime(null);
                          setSelectedCommentId(null);
                          setShowCommentsPanel(true);
                        } else {
                          // Closing panel
                          setShowCommentsPanel(false);
                          setPendingCommentTime(null);
                          setSelectedCommentId(null);
                        }
                      }}
                      className={`relative p-2 rounded-lg transition-colors ${
                        showCommentsPanel
                          ? isDarkMode
                            ? 'bg-brand-yellow text-brand-navy'
                            : 'bg-[#ffe2a6] text-brand-navy'
                          : isDarkMode
                            ? 'hover:bg-gray-700 text-gray-400'
                            : 'hover:bg-gray-100 text-gray-600'
                      }`}
                      title="Toggle comments panel"
                    >
                      <MessageCircle size={16} />
                      {comments.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {comments.length > 99 ? '99+' : comments.length}
                        </span>
                      )}
                    </button>
                  </>
                )}
                
                {/* Playback Speed Control */}
                <div className="relative" ref={speedMenuRef}>
                  <button
                    onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                    className={`flex items-center gap-1 px-2 py-1 rounded transition-colors text-sm font-medium ${
                      isDarkMode 
                        ? 'hover:bg-gray-700 text-gray-300' 
                        : 'hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    <Settings size={16} />
                    <span>{playbackRate}x</span>
                  </button>
                  
                  {showSpeedMenu && (
                    <div className={`absolute bottom-full right-0 mb-2 rounded-md shadow-lg z-50 ${
                      isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                    }`}>
                      {speedOptions.map((speed) => (
                        <button
                          key={speed}
                          onClick={() => {
                            setPlaybackRate(speed);
                            setShowSpeedMenu(false);
                          }}
                          className={`block w-full px-4 py-2 text-sm text-left transition-colors ${
                            playbackRate === speed
                              ? isDarkMode 
                                ? 'bg-brand-yellow text-brand-navy'
                                : 'bg-brand-yellow text-brand-navy'
                              : isDarkMode
                                ? 'hover:bg-gray-700 text-gray-300'
                                : 'hover:bg-gray-100 text-gray-700'
                          }`}
                        >
                          {speed}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Volume control - Hidden on compact touch layouts */}
                {!isCompactTouchLayout && (
                  <div className="flex items-center gap-2">
                    <Volume2 size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-600'} />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                      className="w-16"
                    />
                  </div>
                )}

                <button
                  onClick={() => setIsCollapsed(true)}
                  className={`p-1.5 rounded transition-colors ${
                    isDarkMode 
                      ? 'hover:bg-gray-700 text-gray-400' 
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  title="Minimize player"
                >
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Panel */}
      {sessionId && showCommentsPanel && commentsAllowed && (
        <IsolatedCommentsWrapper
          sessionId={sessionId}
          token={effectiveIsPublicView ? (effectiveShareToken ?? null) : token}
          isPublicView={effectiveIsPublicView}
          onCommentsChange={handleCommentsChange}
          isInline={false}
          pendingTimestamp={pendingCommentTime}
          selectedCommentId={selectedCommentId}
          onClose={handleCloseComments}
          initialComments={initialComments}
          disablePolling={fromCdn && effectiveIsPublicView}
        />
      )}

      {/* References Modal (with edit/delete capabilities) */}
      <ReferenceEditModal
        isOpen={showReferencesModal}
        onClose={() => setShowReferencesModal(false)}
        reference={selectedReference}
        allReferences={ttmlElements.references}
        onSelectReference={(ref) => setSelectedReference(ref as TTMLElement)}
        onSave={handleUpdateReference}
        onDelete={handleDeleteReference}
        onSeek={seekTo}
        isDarkMode={isDarkMode}
        canEdit={canEditTimelineMetadata}
      />

      {/* Add Reference Modal */}
      <AddReferenceModal
        isOpen={showAddReferenceModal}
        onClose={() => setShowAddReferenceModal(false)}
        onSave={handleSaveReference}
        timestamp={addReferenceTimestamp}
        isDarkMode={isDarkMode}
      />

      {/* Floating Action Popup for Clips/Edits */}
      {floatingActionElement && (
        <div
          className={`fixed z-[9999] transform -translate-x-1/2 -translate-y-full pointer-events-auto ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}
          style={{
            left: floatingActionElement.x,
            top: floatingActionElement.y - 8,
          }}
          onMouseEnter={() => {
            // Keep popup visible while hovering over it
            if (floatingActionTimeoutRef.current) {
              clearTimeout(floatingActionTimeoutRef.current);
              floatingActionTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            setFloatingActionElement(null);
          }}
        >
          <div className={`flex items-center gap-1 px-2 py-1.5 rounded-lg shadow-lg border ${
            isDarkMode
              ? 'bg-gray-800 border-gray-600'
              : 'bg-white border-gray-300'
          }`}>
            {/* Edit button */}
            <button
              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                floatingActionElement.type === 'clip'
                  ? isDarkMode
                    ? 'bg-brand-yellow hover:bg-[#f6c15e] text-brand-navy'
                    : 'bg-brand-yellow hover:bg-[#f0a91a] text-brand-navy'
                  : isDarkMode
                    ? 'bg-red-600 hover:bg-red-500 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              onClick={() => {
                setSelectedClipEdit({
                  element: floatingActionElement.element,
                  type: floatingActionElement.type
                });
                setShowClipEditModal(true);
                setFloatingActionElement(null);
              }}
            >
              Edit {floatingActionElement.type === 'clip' ? 'Clip' : 'Edit'}
            </button>
            {/* Seek button */}
            <button
              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
              onClick={() => {
                seekTo(floatingActionElement.element.start);
                setFloatingActionElement(null);
              }}
            >
              Go to
            </button>
            {/* Close button */}
            <button
              className={`p-1 rounded transition-colors ${
                isDarkMode
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
              onClick={() => setFloatingActionElement(null)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Arrow pointing down to the element */}
          <div
            className={`absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent ${
              isDarkMode ? 'border-t-gray-800' : 'border-t-white'
            }`}
          />
        </div>
      )}

      {/* Clip/Edit Modal */}
      <ClipEditModal
        isOpen={showClipEditModal}
        onClose={() => {
          setShowClipEditModal(false);
          setSelectedClipEdit(null);
        }}
        element={selectedClipEdit?.element || null}
        elementType={selectedClipEdit?.type || 'clip'}
        onSave={handleClipEditModalSave}
        onDelete={handleClipEditModalDelete}
        onSeek={seekTo}
        isDarkMode={isDarkMode}
        canEdit={canEditTimelineMetadata}
      />

      {/* Element Drag Tooltip */}
      {elementDragState && elementDragPreview && elementDragTooltipPosition && (
        <TimestampTooltip
          time={elementDragState.handle === 'right' ? elementDragPreview.end : elementDragPreview.start}
          x={elementDragTooltipPosition.x}
          y={elementDragTooltipPosition.y}
          isDarkMode={isDarkMode}
          label={elementDragState.handle === 'move'
            ? `${formatTime(elementDragPreview.start)} - ${formatTime(elementDragPreview.end)}`
            : undefined
          }
        />
      )}

    </>
  );
};

export default memo(CentralAudioPlayer, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Only re-render if these specific props change
  return (
    prevProps.sessionId === nextProps.sessionId &&
    prevProps.speakerSegments === nextProps.speakerSegments &&
    prevProps.speakerLabels === nextProps.speakerLabels &&
    prevProps.transcriptionData === nextProps.transcriptionData &&
    prevProps.ttmlFiles === nextProps.ttmlFiles &&
    prevProps.duration === nextProps.duration &&
    prevProps.selectedTtmlId === nextProps.selectedTtmlId &&
    prevProps.retentionData === nextProps.retentionData &&
    prevProps.searchMatches === nextProps.searchMatches &&
    prevProps.gapClusters === nextProps.gapClusters &&
    prevProps.nonClusteredGaps === nextProps.nonClusteredGaps
  );
});
